import { useStore, DEFAULT_FX } from './store';
import { MODES, DROP_FX, SUBSTANCES, FLIPS, SKINS, PK_PROFILES, FLIP_INTERACTIONS, type PKParams } from './constants';

export const Engine = {
  S: {
    mx: 0.5, my: 0.5, time: 0, last: 0, reseed: 2, fxDose: 0.45,
    trip: null, _fps: 60,
    imgPal: [[0.45,0.25,0.75],[0.30,0.55,0.70],[0.20,0.70,0.55],[0.78,0.42,0.62],[0.88,0.74,0.38],[0.28,0.18,0.42]],
    _imgPalFlat: new Float32Array(18),
    transEnv: 0, transMode: 0, timeline: null as any,
    fx: { ...DEFAULT_FX }
  },
  AUDIO: {
    ctx: null as any, analyser: null as any, data: null as any, src: null as any, stream: null as any,
    peak: { bass: 0.05, mid: 0.05, treble: 0.05, level: 0.05 }, bassSlow: 0, fluxSlow: 0, lastBeatT: 0, iois: [] as number[],
    spec: new Float32Array(96), specPrev: new Float32Array(96), specTex: new Uint8Array(96),
    hiSlow: 0, hiFast: 0, lvlSlow: 0, primed: 0, lastDrop: -99,
    wave: null as any, waveL: null as any, waveR: null as any, waveTex: new Uint8Array(256 * 2).fill(128), wavePeak: 0.1,
    splitter: null as any, anL: null as any, anR: null as any, silentSince: 0, silentWarned: false,
    beatPhase: 0, entrain: null as any,
    // Enhanced audio analysis state
    fluxHistory: null as Float32Array | null,
    chroma: null as Float32Array | null,
    detectedKey: 0,
    isMinor: false,
    beatFrame: 0,
    prevTimeDomain: null as Float32Array | null,
    a: { on: false, beat: 0, flux: 0, build: 0, dropEnv: 0, dropMode: 0, level: 0, bass: 0, mid: 0, treble: 0, centroid: 0, sub: 0, lowmid: 0, highmid: 0, air: 0, bpm: 128, phase: 0, pulseSync: 0, crest: 0, zcr: 0, _lastDropFx:-1, dissonance: 0 },
    // EMA-smoothed band envelopes (prevents jitter, SOTA temporal coherence)
    emaBass: 0, emaMid: 0, emaTreble: 0, emaLevel: 0, emaSub: 0,
    emaLowmid: 0, emaHighmid: 0, emaAir: 0, emaDissonance: 0,
  },
  gl: null as any,
  canvas: null as any,
  programs: {} as any,
  fbo: {} as any,
  particles: null as any,
  req: 0,
  
  init(canvasEl: HTMLCanvasElement) {
    this.canvas = canvasEl;
    this.gl = this.canvas.getContext('webgl2', { antialias: false, preserveDrawingBuffer: true, alpha: false });
    if (!this.gl) {
      alert("WebGL2 not supported.");
      return;
    }
    this.S.last = performance.now() / 1000;
    this.compileShaders();
    this.initFBOs();
    this.resize();
    this.S._imgPalFlat = new Float32Array(this.S.imgPal.flat());
    
    // Bind global events
    window.addEventListener('resize', this.resize.bind(this));
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    window.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });
    
    this.tick();
  },

  destroy() {
    window.removeEventListener('resize', this.resize.bind(this));
    window.removeEventListener('mousemove', this.onMouseMove.bind(this));
    // @ts-ignore
    window.removeEventListener('touchmove', this.onTouchMove.bind(this));
    cancelAnimationFrame(this.req);
    this.stopAudio();
  },

  /* ── Audio ──────────────────────────────────────────────── */

  _fileEl: null as HTMLAudioElement | null,
  _srcNode: MediaElementAudioSourceNode | MediaStreamAudioSourceNode | null,

  async initAudio(source: 'mic' | 'file' = 'mic') {
    this.stopAudio();
    const A = this.AUDIO;
    const AC = A.ctx = new AudioContext();
    A.analyser = AC.createAnalyser();
    A.analyser.fftSize = 256;
    A.analyser.smoothingTimeConstant = 0.82;
    const bufLen = A.analyser.frequencyBinCount;
    A.data = new Uint8Array(bufLen);
    A.spec = new Float32Array(bufLen);
    A.specPrev = new Float32Array(bufLen);
    A.specTex = new Uint8Array(bufLen);
    A.wave = new Float32Array(256);
    A.waveL = new Float32Array(256);
    A.waveR = new Float32Array(256);
    A.waveTex = new Uint8Array(256 * 2).fill(128);

    try {
      if (source === 'mic') {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        A.stream = stream;
        A.src = AC.createMediaStreamSource(stream);
        A.src.connect(A.analyser);
      }
      useStore.getState().updateParams({ audioOn: true, audioSource: source });
      useStore.getState().setAudioStats(`Audio: ${source === 'mic' ? 'microphone' : 'track'}`, false);
    } catch (e: any) {
      useStore.getState().setAudioStats('Audio init failed: ' + (e.message || e), true);
    }
  },

  async loadTrack(file: File) {
    this.stopAudio();
    const A = this.AUDIO;
    const AC = A.ctx || new AudioContext();
    A.ctx = AC;
    A.analyser = AC.createAnalyser();
    A.analyser.fftSize = 256;
    A.analyser.smoothingTimeConstant = 0.82;
    const bufLen = A.analyser.frequencyBinCount;
    A.data = new Uint8Array(bufLen);
    A.spec = new Float32Array(bufLen);
    A.specPrev = new Float32Array(bufLen);
    A.specTex = new Uint8Array(bufLen);
    A.wave = new Float32Array(256);
    A.waveL = new Float32Array(256);
    A.waveR = new Float32Array(256);
    A.waveTex = new Uint8Array(256 * 2).fill(128);

    // Resume the context inside the user-gesture stack. If the AudioContext was
    // created in a suspended state (autoplay policy) and we never resume, the
    // MediaElementAudioSourceNode would silently never reach the destination.
    if (AC.state === 'suspended') {
      try { await AC.resume(); } catch {}
    }

    const url = URL.createObjectURL(file);
    const audio = new Audio();
    // Note: do NOT set crossOrigin here — the blob URL is same-origin and forcing
    // CORS on it makes Chrome reject the load, leaving the track silent.
    audio.src = url;
    audio.loop = true;
    // Hint the browser to keep audio processing alive on background tabs where
    // some engines would otherwise throttle the element to silence.
    audio.setAttribute('playsinline', 'true');
    this._fileEl = audio;

    // keep store in sync with element state — browsers may pause for any reason
    const syncPlaying = () => {
      useStore.getState().updateParams({ trackPlaying: !audio.paused });
    };
    audio.addEventListener('play', syncPlaying);
    audio.addEventListener('pause', syncPlaying);
    audio.addEventListener('ended', syncPlaying);

    // Build the graph FIRST (captures the element's output into WebAudio),
    // then start playback. Doing it in this order avoids the brief moment where
    // audio plays through the default output, gets re-routed into the graph
    // and — on some browsers — drops the first few frames to silence.
    // @ts-ignore
    A.src = AC.createMediaElementSource(audio);
    A.src.connect(A.analyser);
    A.analyser.connect(AC.destination);

    try {
      await audio.play();
    } catch (e: any) {
      useStore.getState().setAudioStats('Play failed: ' + (e?.message || e), true);
      return;
    }
    useStore.getState().updateParams({
      audioOn: true,
      audioSource: 'file',
      trackPlaying: !audio.paused,
    });
    useStore.getState().setAudioStats(`Now playing: ${file.name}`, false);
  },

  /* Toggle play/pause on the currently loaded track. No-op for mic source. */
  async toggleTrackPlay() {
    const audio = this._fileEl;
    if (!audio) return;
    if (audio.paused) {
      try { await audio.play(); }
      catch (e: any) {
        useStore.getState().setAudioStats('Play failed: ' + (e?.message || e), true);
      }
    } else {
      audio.pause();
    }
  },

  stopAudio() {
    const A = this.AUDIO;
    if (this._fileEl) {
      this._fileEl.pause();
      this._fileEl.src = '';
      this._fileEl = null;
    }
    if (A.stream) {
      A.stream.getTracks().forEach(t => t.stop());
      A.stream = null;
    }
    if (A.ctx && A.ctx.state !== 'closed') {
      A.ctx.close().catch(() => {});
    }
    A.ctx = null; A.analyser = null; A.data = null; A.src = null;
    A.a.on = false;
    useStore.getState().updateParams({ audioOn: false, trackPlaying: false });
  },

  analyseAudio() {
    const A = this.AUDIO;
    const st = useStore.getState();
    if (!A.analyser || !A.data) { A.a.on = false; return; }
    // Synchronize visualizations with playback state — when the loaded track is
    // paused we clear the audio stats so the shader falls back to the non-
    // reactive (base-dose) animation path instead of reading frozen frames.
    if (this._fileEl && this._fileEl.paused) {
      A.a.on = false;
      A.a.bass = 0; A.a.mid = 0; A.a.treble = 0; A.a.level = 0;
      A.a.sub = 0; A.a.lowmid = 0; A.a.highmid = 0; A.a.air = 0;
      A.a.beat = 0; A.a.flux = 0; A.a.centroid = 0; A.a.dissonance = 0;
      A.a.dropEnv = 0;
      return;
    }
    A.a.on = true;
    A.analyser.getByteFrequencyData(A.data);
    const n = A.data.length;

    // FFT into bands
    const binHz = (A.ctx?.sampleRate || 44100) / A.analyser.fftSize;
    const freqToIdx = (f: number) => Math.min(n - 1, Math.max(0, Math.round(f / binHz)));
    const avg = (lo: number, hi: number) => {
      let s = 0, c = 0;
      for (let i = freqToIdx(lo); i <= freqToIdx(hi) && i < n; i++) { s += A.data[i]; c++; }
      return c > 0 ? s / c / 255 : 0;
    };

    const sens = st.sensitivity || 1.2;
    const sub   = avg(20, 60) * sens;
    const bass  = avg(60, 250) * sens;
    const lowmid= avg(250, 500) * sens;
    const mid   = avg(500, 2000) * sens;
    const highmid= avg(2000, 4000) * sens;
    const treble= avg(4000, 8000) * sens;
    const air   = avg(8000, 16000) * sens;
    const level = (sub + bass + mid + treble) / 4;

    // spectral centroid
    let cNum = 0, cDen = 0;
    for (let i = 0; i < n; i++) { cNum += A.data[i] * i; cDen += A.data[i]; }
    A.a.centroid = cDen > 0 ? cNum / cDen / n : 0.5;

    // spectral flux
    let flux = 0;
    for (let i = 0; i < n; i++) {
      const d = A.data[i] - A.specPrev[i];
      if (d > 0) flux += d;
      A.specPrev[i] = A.data[i];
    }
    flux /= n * 255;
    A.a.flux = flux;

    // ── AUTOCORRELATION BEAT DETECTION ──
    // Maintain a circular buffer of spectral flux values for tempo estimation
    const fluxHist = A.fluxHistory || new Float32Array(512);
    for (let i = 511; i > 0; i--) fluxHist[i] = fluxHist[i - 1];
    fluxHist[0] = flux;
    A.fluxHistory = fluxHist;

    // Autocorrelation at lag k for tempo estimation (60–180 BPM at 60fps)
    const autocorr = (lag: number) => {
      let sum = 0;
      for (let i = 0; i < 512 - lag; i++) sum += fluxHist[i] * fluxHist[i + lag];
      return sum;
    };

    let maxCorr = 0, bestLag = 30;
    for (let lag = 20; lag <= 60; lag++) {
      const corr = autocorr(lag);
      if (corr > maxCorr) { maxCorr = corr; bestLag = lag; }
    }
    const estimatedBPM = Math.round(3600 / Math.max(bestLag, 1));

    // ── CHROMA FEATURES (pitch class profiling) ──
    const chroma = A.chroma || new Float32Array(12);
    for (let i = 0; i < 12; i++) chroma[i] *= 0.85; // decay
    for (let i = 0; i < n; i++) {
      const freq = i * binHz;
      if (freq < 65 || freq > 4200) continue;
      const midi = 69 + 12 * Math.log2(freq / 440);
      const pitchClass = ((Math.round(midi) % 12) + 12) % 12;
      chroma[pitchClass] += A.data[i] / 255;
    }
    // Normalize
    let chromaMax = 0;
    for (let i = 0; i < 12; i++) chromaMax = Math.max(chromaMax, chroma[i]);
    if (chromaMax > 0) for (let i = 0; i < 12; i++) chroma[i] /= chromaMax;
    A.chroma = chroma;

    // Key detection: correlate with Krumhansl major/minor profiles
    const majorProfile = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
    const minorProfile = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
    let bestKey = 0, bestKeyScore = -1;
    for (let k = 0; k < 12; k++) {
      let majorScore = 0, minorScore = 0;
      for (let i = 0; i < 12; i++) {
        majorScore += chroma[(i + k) % 12] * majorProfile[i];
        minorScore += chroma[(i + k) % 12] * minorProfile[i];
      }
      const score = Math.max(majorScore, minorScore);
      if (score > bestKeyScore) { bestKeyScore = score; bestKey = k; A.isMinor = minorScore > majorScore; }
    }
    A.detectedKey = bestKey;

    // ── CREST FACTOR (peak-to-RMS ratio) ──
    const timeData = new Float32Array(256);
    A.analyser.getFloatTimeDomainData(timeData);
    let rms = 0, peak = 0;
    for (let i = 0; i < 256; i++) {
      const v = Math.abs(timeData[i]);
      rms += v * v;
      if (v > peak) peak = v;
    }
    rms = Math.sqrt(rms / 256);
    A.a.crest = rms > 0.001 ? peak / rms : 1.0;

    // ── ZERO CROSSING RATE ──
    let zcr = 0;
    for (let i = 1; i < 256; i++) {
      if ((timeData[i] >= 0) !== (timeData[i - 1] >= 0)) zcr++;
    }
    A.a.zcr = zcr / 255;

    // beat detection (enhanced with autocorrelation prediction)
    // EMA smoothing (SOTA temporal coherence — prevents jitter)
    const ema = (cur: number, raw: number, fast: number, slow: number) => cur + (raw - cur) * (a.on ? fast : slow);
    A.emaBass = ema(A.emaBass, bass, 0.35, 0.06);
    A.emaMid = ema(A.emaMid, mid, 0.20, 0.04);
    A.emaTreble = ema(A.emaTreble, treble, 0.15, 0.03);
    A.emaLevel = ema(A.emaLevel, level, 0.25, 0.05);
    A.emaSub = ema(A.emaSub, sub, 0.40, 0.06);
    A.emaLowmid = ema(A.emaLowmid, lowmid, 0.22, 0.04);
    A.emaHighmid = ema(A.emaHighmid, highmid, 0.18, 0.03);
    A.emaAir = ema(A.emaAir, air, 0.12, 0.02);

    // Spectral dissonance (roughness → ego-dissolution dread)
    let diss = 0;
    for (let i = 2; i < n; i++) {
      const a_ = A.data[i] / 255, b_ = A.data[i - 2] / 255;
      diss += a_ * b_ * (a_ + b_) * 0.5;
    }
    A.emaDissonance = ema(A.emaDissonance, Math.min(1, diss / n), 0.10, 0.02);
    A.a.dissonance = A.emaDissonance * sens;

    A.a.bass = A.emaBass; A.a.mid = A.emaMid; A.a.treble = A.emaTreble; A.a.level = A.emaLevel;
    A.a.sub = A.emaSub; A.a.lowmid = A.emaLowmid; A.a.highmid = A.emaHighmid; A.a.air = A.emaAir;
    const now = performance.now() / 1000;
    A.bassSlow = A.bassSlow * 0.92 + bass * 0.08;
    const beatHit = (bass > A.bassSlow * 1.35 && bass > 0.18);
    if (beatHit && now - A.lastBeatT > 0.18) {
      A.a.beat = 1.0; A.a.phase = 0; A.lastBeatT = now;
      A.beatFrame = 0;
    } else {
      A.a.beat = Math.max(0, A.a.beat - 0.06);
      A.a.phase = Math.min(1, (now - A.lastBeatT) / Math.max(0.3, 60 / estimatedBPM));
      A.beatFrame = (A.beatFrame || 0) + 1;
    }
    A.a.pulseSync = A.a.beat;
    A.a.bpm = estimatedBPM;

    // waveform time-domain
    const waveData = new Float32Array(256);
    A.analyser.getFloatTimeDomainData(waveData);
    for (let i = 0; i < 256; i++) {
      const s = Math.max(-1, Math.min(1, waveData[i] * 4));
      A.waveTex[i] = Math.round((s * 0.5 + 0.5) * 255);
      A.waveTex[i + 256] = A.waveTex[i]; // mono duplicate
    }
    if (this.fbo.waveTexture && this.gl) {
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.fbo.waveTexture);
      this.gl.texSubImage2D(this.gl.TEXTURE_2D, 0, 0, 0, 256, 1, this.gl.RG, this.gl.UNSIGNED_BYTE, A.waveTex);
    }

    // spectrum texture
    for (let i = 0; i < n; i++) A.specTex[i] = A.data[i];
    if (this.fbo.specTexture && this.gl) {
      this.gl.bindTexture(this.gl.TEXTURE_2D, this.fbo.specTexture);
      this.gl.texSubImage2D(this.gl.TEXTURE_2D, 0, 0, 0, n, 1, this.gl.RED, this.gl.UNSIGNED_BYTE, A.specTex);
    }
  },

  resize() {
    const DPR = Math.min(window.devicePixelRatio || 1, 3);
    const w = Math.floor(window.innerWidth * DPR);
    const h = Math.floor(window.innerHeight * DPR);
    this.canvas.width = w;
    this.canvas.height = h;
    // Render the scene at full canvas resolution — no downscale/upscale.
    this.allocScene(w, h);
    this.S.reseed = 2;
  },

  onMouseMove(e: MouseEvent) {
    this.pointer(e.clientX, e.clientY);
  },
  
  onTouchMove(e: TouchEvent) {
    if (e.touches[0]) this.pointer(e.touches[0].clientX, e.touches[0].clientY);
  },

  pointer(x: number, y: number) {
    this.S.mx = x / window.innerWidth;
    this.S.my = 1.0 - y / window.innerHeight;
  },

  // GL Plumbing
  compile(type: number, src: string) {
    const s = this.gl.createShader(type);
    this.gl.shaderSource(s, src);
    this.gl.compileShader(s);
    if (!this.gl.getShaderParameter(s, this.gl.COMPILE_STATUS)) {
      console.error(this.gl.getShaderInfoLog(s));
      throw new Error('shader compile failed');
    }
    return s;
  },

  program(vsrc: string, fsrc: string) {
    const p = this.gl.createProgram();
    this.gl.attachShader(p, this.compile(this.gl.VERTEX_SHADER, vsrc));
    this.gl.attachShader(p, this.compile(this.gl.FRAGMENT_SHADER, fsrc));
    this.gl.bindAttribLocation(p, 0, 'aPos');
    this.gl.linkProgram(p);
    if (!this.gl.getProgramParameter(p, this.gl.LINK_STATUS)) {
      throw new Error('link failed: ' + this.gl.getProgramInfoLog(p));
    }
    return p;
  },

  makeTarget(w: number, h: number, wrapT?: number) {
    const gl = this.gl;
    const floatOK = !!gl.getExtension('EXT_color_buffer_float');
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    const internal = floatOK ? gl.RGBA16F : gl.RGBA;
    const type = floatOK ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE;
    gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, gl.RGBA, type, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrapT || gl.CLAMP_TO_EDGE);
    const fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    return { tex, fbo };
  },

  allocScene(w: number, h: number) {
    this.fbo.sceneW = w;
    this.fbo.sceneH = h;
    this.fbo.sceneA = this.makeTarget(w, h);
    this.fbo.sceneB = this.makeTarget(w, h);
    this.fbo.transTex = this.makeTarget(w, h);
    // HDR bloom: half-resolution FBOs for bright-extract + two-pass Gaussian blur
    const bw = Math.max(1, w >> 1), bh = Math.max(1, h >> 1);
    this.fbo.bloomA = this.makeTarget(bw, bh);
    this.fbo.bloomB = this.makeTarget(bw, bh);
    this.fbo.bloomW = bw; this.fbo.bloomH = bh;
  },

  initFBOs() {
    const gl = this.gl;
    this.fbo.vao = gl.createVertexArray();
    gl.bindVertexArray(this.fbo.vao);
    const vbo = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const FIELD_N = 512;
    this.fbo.fieldA = this.makeTarget(FIELD_N, FIELD_N, gl.REPEAT);
    this.fbo.fieldB = this.makeTarget(FIELD_N, FIELD_N, gl.REPEAT);

    const specTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, specTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, 96, 1, 0, gl.RED, gl.UNSIGNED_BYTE, new Uint8Array(96));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.fbo.specTexture = specTex;

    const waveTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, waveTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RG8, 256, 1, 0, gl.RG, gl.UNSIGNED_BYTE, new Uint8Array(256 * 2).fill(128));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.fbo.waveTexture = waveTex;

    const imgTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, imgTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 2, 2, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([120,80,200,255, 60,180,160,255, 220,120,160,255, 230,200,90,255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
    this.fbo.imgTexture = imgTex;
  },

  U(p: any, n: string) { return this.gl.getUniformLocation(p, n); },

  setCommon(p: any, rx: number, ry: number) {
    const st = useStore.getState();
    const sf = this.S;
    const gl = this.gl;
    const a = this.AUDIO.a;

    gl.uniform2f(this.U(p,'uRes'), rx, ry);
    gl.uniform1f(this.U(p,'uTime'), sf.time);
    gl.uniform1f(this.U(p,'uDose'), sf.fxDose);
    gl.uniform1f(this.U(p,'uSpeed'), st.speed);
    gl.uniform1f(this.U(p,'uCplx'), Math.min(1.0, st.cplx + st.asc*0.4));
    gl.uniform1f(this.U(p,'uSym'), Math.min(18.0, st.sym + st.asc*6.0));
    gl.uniform1f(this.U(p,'uWarp'), st.mouse ? 1.0 : 0.0);
    gl.uniform1i(this.U(p,'uMode'), st.mode);
    gl.uniform1i(this.U(p,'uPalette'), st.palette);
    gl.uniform1i(this.U(p,'uPlanform'), st.planform);
    gl.uniform2f(this.U(p,'uMouse'), sf.mx, sf.my);
    gl.uniform1f(this.U(p,'uFlicker'), st.flicker ? 1.0 : 0.0);
    gl.uniform1f(this.U(p,'uFlickerHz'), st.fhz);

    const fx = sf.fx;
    const ar = a.on ? st.reactivity : 0.0;
    gl.uniform1f(this.U(p,'uHueShift'), fx.hue + sf.time*fx.hueRate);
    gl.uniform1f(this.U(p,'uPulse'), fx.pulse + (a.on ? a.beat*st.reactivity*0.28 : 0.0));
    gl.uniform1f(this.U(p,'uPulseRate'), fx.pulseRate);
    gl.uniform1f(this.U(p,'uShear'), fx.shear * st.dose);
    gl.uniform1f(this.U(p,'uVoid'), fx.voidness);
    
    gl.uniform4f(this.U(p,'uAudio'), a.level*ar, a.bass*ar, a.mid*ar, a.treble*ar);
    gl.uniform1f(this.U(p,'uBeat'), a.beat*ar);
    gl.uniform1f(this.U(p,'uBeatPulse'), a.on ? (a.pulseSync||0)*ar : 0.0);
    gl.uniform1f(this.U(p,'uBeatPhase'), a.on ? (a.phase||0) : 0.0);
    gl.uniform1f(this.U(p,'uCentroid'), a.on ? a.centroid : 0.0);
    gl.uniform1f(this.U(p,'uFlux'), a.on ? a.flux*st.reactivity : 0.0);
    gl.uniform1f(this.U(p,'uDissonance'), a.on ? a.dissonance*st.reactivity : 0.0);
    gl.uniform1f(this.U(p,'uAudioAmt'), ar);
    gl.uniform1f(this.U(p,'uRay'), st.ray ? 1.0 : 0.0);
    gl.uniform1f(this.U(p,'uChoreo'), st.choreo ? 1.0 : 0.0);
    gl.uniform1f(this.U(p,'uBloomT'), a.on ? Math.min(3.0, sf.time - this.AUDIO.lastBeatT) : 3.0);
    gl.uniform2f(this.U(p,'uStereo'), a.bass*ar, a.treble*ar);
    gl.uniform4f(this.U(p,'uBands0'), a.sub*ar, a.bass*ar, a.lowmid*ar, a.mid*ar);
    gl.uniform4f(this.U(p,'uBands1'), a.highmid*ar, a.treble*ar, a.air*ar, a.level*ar);
    gl.uniform1f(this.U(p,'uTone'), st.tone);
    gl.uniform1f(this.U(p,'uAsc'), Math.min(1.2, st.asc + (a.dropEnv||0)*0.5));
    gl.uniform1f(this.U(p,'uBuild'), (st.drop && a.on) ? (a.build||0) : 0.0);
    gl.uniform1f(this.U(p,'uDrop'), a.dropEnv||0);
    gl.uniform1i(this.U(p,'uDropMode'), a.dropMode||0);
    gl.uniform3fv(this.U(p,'uImgPal'), sf._imgPalFlat);
    gl.uniform1i(this.U(p,'uWall'), st.wall);
    gl.uniform1f(this.U(p,'uWallScale'), st.wallScale);
    gl.uniform1f(this.U(p,'uScope'), st.scope ? 1.0 : 0.0);
    gl.uniform1f(this.U(p,'uCine'), st.cine ? 0.85 : 0.0);
    gl.uniform1f(this.U(p,'uTrans'), sf.transEnv||0.0);
    gl.uniform1i(this.U(p,'uTransMode'), sf.transMode||0);
  },

  tick() {
    this.req = requestAnimationFrame(this.tick.bind(this));
    const now = performance.now() / 1000;
    let dt = now - this.S.last;
    this.S.last = now;
    if (dt > 0.1) dt = 0.1;
    this.S.time += dt;

    if (this.S.transEnv > 0) this.S.transEnv = Math.max(0, this.S.transEnv - dt / 0.7);
    this.S._fps = this.S._fps * 0.92 + (1 / Math.max(dt, 1e-3)) * 0.08;

    const st = useStore.getState();
    if (st.journey) this.autoJourney(dt);

    // Audio analysis every frame
    this.analyseAudio();

    // Update pharmacokinetic model (returns receptor occupancy 0–1)
    const pkRO = this.updatePK(dt);

    const dose = st.dose;
    const aBoost = this.AUDIO.a.on ? this.AUDIO.a.bass * st.reactivity * 0.45 : 0.0;
    // Blend manual dose with PK receptor occupancy when a substance is active
    const effectiveDose = this.pk.active ? Math.min(1.0, dose * 0.4 + pkRO * 0.6) : dose;
    this.S.fxDose = this.approachTau(this.S.fxDose, Math.min(1.0, effectiveDose + aBoost), 0.10, dt);
    
    // Core render loop here
    const gl = this.gl;
    const fbo = this.fbo;
    if(!fbo.vao || !this.programs.compose) return;

    const feedback = Math.min(0.985, st.trail + this.S.fxDose * this.S.fx.feedGain + st.asc * 0.25 + (this.AUDIO.a.dropEnv || 0) * 0.3);

    gl.bindVertexArray(fbo.vao);

    if (st.mode === 1 || st.mode === 2 || st.mode === 13) {
      const sub = st.mode === 2 ? 14 : st.mode === 13 ? 2 : 3;
      gl.useProgram(this.programs.field);
      gl.viewport(0, 0, 512, 512);
      for (let i = 0; i < sub; i++) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.fieldB.fbo);
        this.setCommon(this.programs.field, 512, 512);
        gl.uniform1f(this.U(this.programs.field, 'uReset'), this.S.reseed > 0 ? 1.0 : 0.0);
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, fbo.fieldA.tex);
        gl.uniform1i(this.U(this.programs.field, 'uPrev'), 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        const tmp = fbo.fieldA; fbo.fieldA = fbo.fieldB; fbo.fieldB = tmp;
        if (this.S.reseed > 0) this.S.reseed--;
      }
    }

    gl.useProgram(this.programs.compose);
    gl.viewport(0, 0, fbo.sceneW, fbo.sceneH);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.sceneB.fbo);
    this.setCommon(this.programs.compose, fbo.sceneW, fbo.sceneH);
    gl.uniform1f(this.U(this.programs.compose, 'uFeedback'), feedback);
    gl.uniform1f(this.U(this.programs.compose, 'uFeedZoom'), 1.0 - this.S.fxDose * this.S.fx.zoom);
    gl.uniform1f(this.U(this.programs.compose, 'uFeedRot'), this.S.fxDose * 0.004 * this.S.fx.rot * Math.sin(this.S.time * 0.3));
    
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, fbo.sceneA.tex); gl.uniform1i(this.U(this.programs.compose, 'uScenePrev'), 0);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, fbo.fieldA.tex); gl.uniform1i(this.U(this.programs.compose, 'uField'), 1);
    gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, fbo.specTexture); gl.uniform1i(this.U(this.programs.compose, 'uSpectrum'), 2);
    gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, fbo.imgTexture); gl.uniform1i(this.U(this.programs.compose, 'uImage'), 3);
    gl.activeTexture(gl.TEXTURE4); gl.bindTexture(gl.TEXTURE_2D, fbo.waveTexture); gl.uniform1i(this.U(this.programs.compose, 'uWaveform'), 4);
    
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    const ts = fbo.sceneA; fbo.sceneA = fbo.sceneB; fbo.sceneB = ts;

    // ── HDR BLOOM PASS (SOTA: bright extract + two-pass Gaussian at half-res) ──
    if (fbo.bloomA && fbo.bloomB && this.programs.bloomBright && this.programs.bloomBlur) {
      // 1. Extract bright pixels from scene → bloomA
      gl.useProgram(this.programs.bloomBright);
      gl.viewport(0, 0, fbo.bloomW, fbo.bloomH);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.bloomA.fbo);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, fbo.sceneA.tex);
      gl.uniform1i(this.U(this.programs.bloomBright, 'uScene'), 0);
      gl.uniform2f(this.U(this.programs.bloomBright, 'uRes'), fbo.bloomW, fbo.bloomH);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      // 2. Horizontal blur bloomA → bloomB
      gl.useProgram(this.programs.bloomBlur);
      gl.viewport(0, 0, fbo.bloomW, fbo.bloomH);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.bloomB.fbo);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, fbo.bloomA.tex);
      gl.uniform1i(this.U(this.programs.bloomBlur, 'uTex'), 0);
      gl.uniform2f(this.U(this.programs.bloomBlur, 'uDir'), 1.0 / fbo.bloomW, 0.0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      // 3. Vertical blur bloomB → bloomA
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.bloomA.fbo);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, fbo.bloomB.tex);
      gl.uniform1i(this.U(this.programs.bloomBlur, 'uTex'), 0);
      gl.uniform2f(this.U(this.programs.bloomBlur, 'uDir'), 0.0, 1.0 / fbo.bloomH);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      // 4. Second blur pass for wider bloom: horizontal + vertical
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.bloomB.fbo);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, fbo.bloomA.tex);
      gl.uniform1i(this.U(this.programs.bloomBlur, 'uTex'), 0);
      gl.uniform2f(this.U(this.programs.bloomBlur, 'uDir'), 2.0 / fbo.bloomW, 0.0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.bloomA.fbo);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, fbo.bloomB.tex);
      gl.uniform1i(this.U(this.programs.bloomBlur, 'uTex'), 0);
      gl.uniform2f(this.U(this.programs.bloomBlur, 'uDir'), 0.0, 2.0 / fbo.bloomH);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    gl.useProgram(this.programs.present);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    this.setCommon(this.programs.present, this.canvas.width, this.canvas.height);
    
    gl.uniform1f(this.U(this.programs.present, 'uGrain'), this.S.fx.grainBase + this.S.fxDose * this.S.fx.grainGain + st.asc * 0.04);
    gl.uniform1f(this.U(this.programs.present, 'uChroma'), this.S.fx.chromaBase + this.S.fxDose * this.S.fx.chromaGain + st.asc * 1.2);
    gl.uniform1f(this.U(this.programs.present, 'uVignette'), 0.55);
    gl.uniform1f(this.U(this.programs.present, 'uSat'), this.S.fx.satBase + this.S.fxDose * this.S.fx.satGain);
    gl.uniform1f(this.U(this.programs.present, 'uBloomAmt'), 0.6 + this.S.fxDose * 0.8 + st.asc * 0.4);
    
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, fbo.sceneA.tex); gl.uniform1i(this.U(this.programs.present, 'uScene'), 0);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, fbo.waveTexture); gl.uniform1i(this.U(this.programs.present, 'uWaveform'), 1);
    gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, fbo.transTex.tex); gl.uniform1i(this.U(this.programs.present, 'uTransTex'), 2);
    gl.activeTexture(gl.TEXTURE5); gl.bindTexture(gl.TEXTURE_2D, fbo.bloomA.tex); gl.uniform1i(this.U(this.programs.present, 'uBloom'), 5);
    
    gl.drawArrays(gl.TRIANGLES, 0, 3);

    // Sync Stats
    // Update Math HUD periodically
    if (st.math) {
      if (!this._mc) this._mc = 0;
      this._mc++;
      if (this._mc % 6 === 0) {
        useStore.getState().setMathStats(this.getMathHUD());
      }
    }
  },

  _mc: 0,
  jT: 0,
  jNext: 14,
  
  approachTau(cur: number, tgt: number, tau: number, dt: number) {
    return cur + (tgt - cur) * (1.0 - Math.exp(-dt / Math.max(tau, 1e-3)));
  },

  /* ── 2-Compartment Pharmacokinetic Model ──────────────────── */
  pk: {
    A: 0, C1: 0, C2: 0, RO: 0,
    tLast: 0, active: false,
    profile: null as PKParams | null,
    substanceId: '',
  },

  dose(substanceId: string, doseMg: number) {
    const profile = PK_PROFILES[substanceId];
    if (!profile) return;
    this.pk = {
      A: doseMg * profile.F * 1000,   // μg in gut
      C1: 0, C2: 0, RO: 0,
      tLast: performance.now() / 1000,
      active: true, profile, substanceId,
    };
  },

  updatePK(dt: number): number {
    const pk = this.pk;
    if (!pk.active || !pk.profile) return 0;
    const p = pk.profile;
    const dt_h = dt / 3600;   // convert seconds → hours

    // 2-compartment ODEs (Euler integration)
    const dA  = -p.ka * pk.A;
    const dC1 = (p.ka * pk.A / p.Vc - p.k10 * pk.C1 - p.k12 * pk.C1 + p.k21 * pk.C2) * dt_h;
    const dC2 = (p.k12 * pk.C1 - p.k21 * pk.C2) * dt_h;

    pk.A  = Math.max(0, pk.A + dA * dt_h);
    pk.C1 = Math.max(0, pk.C1 + dC1);
    pk.C2 = Math.max(0, pk.C2 + dC2);

    // Receptor occupancy (Hill equation)
    const conc = pk.C1;
    pk.RO = Math.pow(conc, p.Hill) / (Math.pow(p.EC50, p.Hill) + Math.pow(conc, p.Hill));

    // Auto-deactivate when absorption complete and plasma declining
    if (pk.A < 0.01 && pk.C1 < 0.001 && pk.C2 < 0.001) {
      pk.active = false;
      pk.RO = 0;
    }
    return pk.RO;
  },

  getFlipIntensity(flipId: string): number {
    const interaction = FLIP_INTERACTIONS[flipId];
    if (!interaction) return 1.0;
    // Scale by current PK occupancy if applicable
    return interaction.intensity * (0.7 + 0.3 * (this.pk.active ? this.pk.RO : 1.0));
  },

  autoJourney(dt: number) {
    this.jT += dt;
    const st = useStore.getState();
    const ch = 0.5 + st.chaos * 1.5;
    
    useStore.setState({ asc: Math.max(0, Math.min(1, 0.42 + 0.32 * Math.sin(this.S.time * 0.04 * ch) + (this.AUDIO.a.on ? this.AUDIO.a.level * 0.14 : 0))) });
    useStore.setState({ dose: Math.max(0, Math.min(1, 0.48 + 0.30 * Math.sin(this.S.time * 0.05 * ch) + (this.AUDIO.a.on ? this.AUDIO.a.bass * 0.20 : 0))) });
    useStore.setState({ cplx: Math.max(0, Math.min(1, 0.5 + 0.4 * Math.sin(this.S.time * 0.037 * ch + 1.0))) });
    useStore.setState({ sym: 4 + Math.round(8 * (0.5 + 0.5 * Math.sin(this.S.time * 0.02 * ch))) });
    
    const beatHit = this.AUDIO.a.on && this.AUDIO.a.beat > 0.8;
    if (this.jT > this.jNext || (beatHit && this.jT > 7.0)) {
      this.jT = 0;
      this.jNext = (14 + Math.random() * 10) / ch;
      if (Math.random() < 0.7) useStore.setState({ mode: Math.floor(Math.random() * MODES.length) });
      if (Math.random() < 0.6) useStore.setState({ palette: Math.floor(Math.random() * 9) });
      if (Math.random() < 0.4) useStore.setState({ planform: Math.floor(Math.random() * 4) });
    }
  },

  getMathHUD() {
    return `Fps: ${this.S._fps|0} · mode: ${useStore.getState().mode}`;
  },

  compileShaders() {
    const VERT = `#version 300 es
in vec2 aPos; out vec2 vUv;
void main(){ vUv = aPos*0.5+0.5; gl_Position = vec4(aPos,0.,1.); }`;

    const COMMON = `
precision highp float;
#define PI 3.14159265359
#define TAU 6.28318530718
uniform vec2  uRes;
uniform float uTime, uDose, uSpeed, uCplx, uSym, uWarp;
uniform int   uMode, uPalette, uPlanform;
uniform vec2  uMouse;
uniform float uFlicker, uFlickerHz;
uniform float uHueShift, uPulse, uPulseRate, uShear, uVoid;
uniform vec4  uAudio;   // (level, bass, mid, treble), pre-scaled by reactivity (0 when off)
uniform float uBeat;    // transient onset 0..1
uniform float uCentroid, uFlux, uAudioAmt, uDissonance;  // spectral centroid, flux/onset, reactivity gate, spectral dissonance
uniform float uRay, uChoreo, uBloomT;   // 3D raymarch toggle, choreography toggle, time-since-beat
uniform vec2  uStereo;                   // (bass, treble) for spatial split
uniform vec4  uBands0;                    // (sub, bass, low-mid, mid)  — react-scaled
uniform vec4  uBands1;                    // (high-mid, treble, air, level)
uniform float uTone;                     // tone/depth: shadow-crush contrast
uniform float uBeatPulse, uBeatPhase;     // grid-locked beat (predictive sync): pulse 1→0 each beat, phase 0..1
uniform float uBuild, uDrop;              // drop engine: build/anticipation 0..1, drop blast envelope 1→0
uniform int   uDropMode;                   // which drop effect fires (0..8)
uniform float uAsc;                       // ✦ ASCENSION macro (0..1) — takes it to 11
uniform sampler2D uSpectrum;
uniform sampler2D uImage;                 // uploaded image (PRO)
uniform vec3  uImgPal[6];                 // palette extracted from the image (linear)
uniform int   uWall;                      // wallpaper-group lens: 0 off · 1 pmm · 2 p4m · 3 p6m · 4 p6
uniform float uWallScale;                 // lens lattice density (cells across)
uniform sampler2D uWaveform;              // time-domain oscilloscope (R=L/mono, G=R) — the actual wave
uniform float uScope;                      // scope overlay toggle (live waveform trace)
uniform float uCine;                       // cinematic post-FX strength: anamorphic streaks + god-rays + halation

mat2 rot(float a){ float c=cos(a),s=sin(a); return mat2(c,-s,s,c); }
vec2 cdiv(vec2 a, vec2 b){ float d=dot(b,b)+1e-9; return vec2(a.x*b.x+a.y*b.y, a.y*b.x-a.x*b.y)/d; }
float hash21(vec2 p){ p=fract(p*vec2(123.34,345.45)); p+=dot(p,p+34.345); return fract(p.x*p.y); }
float vnoise(vec2 p){
  vec2 i=floor(p), f=fract(p);
  float a=hash21(i), b=hash21(i+vec2(1,0)), c=hash21(i+vec2(0,1)), d=hash21(i+vec2(1,1));
  vec2 u=f*f*(3.-2.*f);
  return mix(mix(a,b,u.x),mix(c,d,u.x),u.y);
}
float fbm(vec2 p, int oct){
  float s=0.,a=0.5; mat2 m=mat2(1.6,1.2,-1.2,1.6);
  for(int i=0;i<8;i++){ if(i>=oct) break; s+=a*vnoise(p); p=m*p+vec2(1.7,9.2); a*=0.5; }
  return s;
}
int octaves(){ return int(mix(3.0,7.0,uCplx)); }
vec2 fold(vec2 p, float n){ float a=atan(p.y,p.x), r=length(p); a=mod(a,TAU/n); a=abs(a-PI/n); return r*vec2(cos(a),sin(a)); }

/* ---- OKLab (Ottosson) for perceptual colour ---- */
vec3 oklab2lin(vec3 L){
  float l_=L.x+0.3963377774*L.y+0.2158037573*L.z;
  float m_=L.x-0.1055613458*L.y-0.0638541728*L.z;
  float s_=L.x-0.0894841775*L.y-1.2914855480*L.z;
  float l=l_*l_*l_, m=m_*m_*m_, s=s_*s_*s_;
  return vec3( 4.0767416621*l-3.3077115913*m+0.2309699292*s,
              -1.2684380046*l+2.6097574011*m-0.3413193965*s,
              -0.0041960863*l-0.7034186147*m+1.7076147010*s);
}
vec3 lin2oklab(vec3 c){
  c=max(c,0.0);
  float l=0.4122214708*c.r+0.5363325363*c.g+0.0514459929*c.b;
  float m=0.2119034982*c.r+0.6806995451*c.g+0.1073969566*c.b;
  float s=0.0883024619*c.r+0.2817188376*c.g+0.6299787005*c.b;
  l=pow(l,1.0/3.0); m=pow(m,1.0/3.0); s=pow(s,1.0/3.0);
  return vec3(0.2104542553*l+0.7936177850*m-0.0040720468*s,
              1.9779984951*l-2.4285922050*m+0.4505937099*s,
              0.0259040371*l+0.7827717662*m-0.8086757660*s);
}
vec3 oklch(float L, float C, float h){ return max(oklab2lin(vec3(L, C*cos(h), C*sin(h))), 0.0); }
vec3 PAL(float t, int id){
  t=fract(t);
  float Lw = 0.5 + 0.34*cos(t*TAU - 1.0); 
  if(id==0) return oklch(Lw, 0.17, t*TAU);                                      
  if(id==1) return oklch(mix(0.30,0.86,t), 0.18, mix(0.45,5.70,t));            
  if(id==2) return oklch(mix(0.14,0.96,t), mix(0.08,0.21,t), mix(0.10,1.60,t));
  if(id==3) return oklch(mix(0.16,0.88,t), 0.15, mix(3.20,4.85,t));            
  if(id==4) return oklch(mix(0.22,0.90,t), 0.16, mix(5.0,1.6,0.5+0.5*cos(t*TAU))); 
  if(id==5) return oklch(Lw*1.08, 0.23, t*TAU);                                 
  if(id==6) return oklch(mix(0.03,0.92,pow(t,1.6)), 0.06+0.10*t, mix(0.7,1.1,t)); 
  if(id==7) return oklch(mix(0.05,0.78,t), mix(0.10,0.24,t), mix(4.7,6.0,t));   
  if(id==8) return oklch(mix(0.03,0.90,pow(t,1.3)), mix(0.10,0.25,t), mix(0.02,0.95,t)); 
  float f=clamp(t,0.0,0.999)*5.0; int i=int(f); return mix(uImgPal[i], uImgPal[i+1], fract(f)); 
}
vec3 hueShift(vec3 c, float a){ const vec3 k=vec3(0.57735); float ca=cos(a); return c*ca + cross(k,c)*sin(a) + k*dot(k,c)*(1.0-ca); }
vec3 aces(vec3 x){ return clamp((x*(2.51*x+0.03))/(x*(2.43*x+0.59)+0.14), 0.0, 1.0); }
`;

    const FIELD = `#version 300 es
${COMMON}
uniform sampler2D uPrev; uniform float uReset;
in vec2 vUv; out vec4 frag;
void main(){
  vec2 px=1.0/uRes;
  if(uMode==1){                                   // ---- neural field (Amari/Wilson–Cowan) ----
    if(uReset>0.5){ frag=vec4((hash21(vUv*vec2(311.0,517.0))-0.5)*0.7,0.,0.,1.); return; }
    float gain=mix(1.2,5.0,uDose), mu=mix(2.0,10.0,uDose);
    float se=1.7, si=3.4;                          
    float accE=0.0,wE=0.0,accI=0.0,wI=0.0;
    for(int j=-4;j<=4;j++) for(int i=-4;i<=4;i++){
      vec2 o=vec2(float(i),float(j)); float d2=dot(o,o);
      float a=texture(uPrev, vUv+o*px).x;
      float f=1.0/(1.0+exp(-gain*a))-0.5;         
      float we=exp(-d2/(2.0*se*se)), wi=exp(-d2/(2.0*si*si));
      accE+=we*f; wE+=we; accI+=wi*f; wI+=wi;
    }
    float conv=accE/wE - accI/wI;                  
    float a=texture(uPrev,vUv).x;
    float h=uFlicker*0.10*sin(uTime*TAU*uFlickerHz);
    a += 0.45*(-a + mu*conv + h);                  
    frag=vec4(clamp(a,-1.0,1.0),0.,0.,1.); return;
  }
  if(uMode==2){                                    // ---- Gray–Scott reaction–diffusion ----
    if(uReset>0.5){ float b=step(0.6,hash21(floor(vUv*36.0)+1.3)); frag=vec4(1.0,b*0.9,0.,1.); return; }
    vec2 c=texture(uPrev,vUv).xy;
    vec2 l=(texture(uPrev,vUv+vec2(-px.x,0)).xy+texture(uPrev,vUv+vec2(px.x,0)).xy
           +texture(uPrev,vUv+vec2(0,-px.y)).xy+texture(uPrev,vUv+vec2(0,px.y)).xy)*0.2
          +(texture(uPrev,vUv+vec2(-px.x,-px.y)).xy+texture(uPrev,vUv+vec2(px.x,-px.y)).xy
           +texture(uPrev,vUv+vec2(-px.x,px.y)).xy+texture(uPrev,vUv+vec2(px.x,px.y)).xy)*0.05 - c;
    float U=c.x,V=c.y, f=mix(0.030,0.058,uCplx), k=0.062, uvv=U*V*V;
    frag=vec4(clamp(U+(l.x-uvv+f*(1.0-U)),0.,1.), clamp(V+(0.5*l.y+uvv-(f+k)*V),0.,1.), 0.,1.); return;
  }
  if(uMode==13){                                  // ---- lizard-scale CA: discrete Turing (Young model) ----
    if(uReset>0.5){ frag=vec4(step(0.5,hash21(floor(vUv*190.0)+2.7)),0.,0.,1.); return; }
    float s=texture(uPrev,vUv).x;
    float ae=0.0,we=0.0,ai=0.0,wi=0.0;            
    for(int j=-4;j<=4;j++) for(int i=-4;i<=4;i++){
      vec2 o=vec2(float(i),float(j)); float d2=dot(o,o);
      float v=texture(uPrev,vUv+o*px).x;
      float ke=exp(-d2/(2.0*1.3*1.3)), ki=exp(-d2/(2.0*3.4*3.4));
      ae+=ke*v; we+=ke; ai+=ki*v; wi+=ki;
    }
    float drive=ae/we - ai/wi;                     
    float target=step(0.0,drive);                  
    float rate=0.12+0.30*uDose;                    
    float jit=(hash21(vUv*vec2(421.0,977.0)+fract(uTime*0.7))-0.5)*0.18; 
    frag=vec4(clamp(mix(s,target,rate)+jit*rate,0.0,1.0),0.,0.,1.); return;
  }
  frag=vec4(0.);
}`;

    const COMPOSE = `#version 300 es
${COMMON}
uniform sampler2D uScenePrev, uField;
uniform float uFeedback, uFeedZoom, uFeedRot;
in vec2 vUv; out vec4 frag;

vec2 warpToMouse(vec2 p){
  if(uWarp<=0.0) return p;
  vec2 m=(uMouse-0.5)*2.0; m.x*=uRes.x/uRes.y;
  vec2 d=p-m; float r=length(d)+0.001;
  return p - normalize(d)*uWarp*0.25*exp(-r*1.6);
}
float cortX(float r){ return log(1.0 + (0.051/0.087)*r); }  // Schwartz radial (foveal magnification)

vec3 formMode(vec2 p, float t){
  p=warpToMouse(p);
  float r=length(p)+1e-3, th=atan(p.y,p.x)+t*0.15, cx=cortX(r);
  float kr=mix(14.0,46.0,uCplx), m=floor(mix(3.0,12.0,uCplx));
  float tunnel=cos(kr*cx - t*1.3);
  float spiral=cos(kr*cx + m*th - t);
  float fan   =cos(m*th - t*1.1);
  float lattice=cos(kr*cx+m*th-t)*cos(kr*cx-m*th-t);
  float v;
  if(uPlanform==1) v=tunnel;
  else if(uPlanform==2) v=spiral;
  else if(uPlanform==3) v=lattice;
  else { float s1=0.5+0.5*sin(t*0.13),s2=0.5+0.5*sin(t*0.09+1.7),s3=0.5+0.5*sin(t*0.17+3.0);
    v=mix(tunnel,spiral,s1); v=mix(v,fan,s2*0.5); v=mix(v,lattice,s3*0.5); }
  float fl=uFlicker*0.22*(0.5+0.5*sin(uTime*TAU*uFlickerHz)); // flicker elicits the geometry (gentle)
  float act=smoothstep(-0.2,0.45,v+fl);
  vec3 col=PAL(fract(0.55*v+0.18*cx - t*0.05), uPalette)*(0.4+0.75*act);
  return col*smoothstep(0.0,0.10,r);
}
vec3 neuralMode(vec2 p, float t){
  p=warpToMouse(p);
  float r=length(p)+1e-3, th=atan(p.y,p.x)+t*0.08, cx=cortX(r);
  vec2 fuv=vec2(clamp(cx/0.95,0.0,1.0), fract((th+PI)/TAU));
  float a=texture(uField,fuv).x;
  float act=smoothstep(-0.05,0.4,a);
  vec3 col=PAL(fract(a*0.8+0.15*cx - t*0.03), uPalette)*(0.32+0.95*act);
  return col*smoothstep(0.0,0.10,r);
}
vec3 turingMode(){
  vec2 uv=gl_FragCoord.xy/uRes; float asp=uRes.x/uRes.y;
  vec2 c=uv-0.5; if(asp>1.0) c.x*=asp; else c.y/=asp; uv=clamp(c+0.5,0.0,1.0);
  vec2 px=1.0/uRes;
  float v=texture(uField,uv).y;
  float gx=texture(uField,uv+vec2(px.x,0)).y-texture(uField,uv-vec2(px.x,0)).y;
  float gy=texture(uField,uv+vec2(0,px.y)).y-texture(uField,uv-vec2(0,px.y)).y;
  float e=clamp(length(vec2(gx,gy))*7.0,0.0,1.0);
  return PAL(fract(v*3.0+0.1+uTime*0.02),uPalette)*(0.3+v*2.2)+e*0.3;
}
vec3 sacred(vec2 p, float t){
  p=warpToMouse(p);
  vec2 q=fold(rot(t*0.08)*p*(1.0+0.18*sin(t*0.5)), uSym); int oc=octaves();
  float pat=fbm(q*3.0+vec2(t*0.18,0.0),oc)+0.5*fbm(q*6.0-t*0.1,oc);
  float glow=0.0; const float GA=2.39996323; int cnt=int(mix(28.0,90.0,uCplx));
  for(int i=1;i<=90;i++){ if(i>cnt) break; float fi=float(i);
    vec2 c=0.045*sqrt(fi)*vec2(cos(fi*GA+t*0.25),sin(fi*GA+t*0.25));
    glow+=smoothstep(0.03,0.0,length(q-fold(c,uSym))); }
  vec3 col=PAL(fract(pat+0.12*t),uPalette)*(0.5+0.6*pat);
  return col+PAL(fract(0.3+t*0.1),uPalette)*glow*0.9;
}
vec3 breathing(vec2 p, float t){
  p=warpToMouse(p); int oc=octaves();
  vec2 q=vec2(fbm(p+vec2(0.0,t*0.12),oc), fbm(p+vec2(5.2,1.3)-t*0.10,oc));
  vec2 b=vec2(fbm(p+4.0*q+vec2(1.7,9.2)+t*0.15,oc), fbm(p+4.0*q+vec2(8.3,2.8),oc));
  float f=fbm(p+4.0*b, oc);
  vec3 col=PAL(fract(f+0.18*length(b)-0.04*t), uPalette);
  col=mix(col, PAL(fract(0.5+length(q)*0.6), uPalette), 0.35);
  return col*(0.45+0.85*f);
}
vec3 hyperspace(vec2 p, float t){
  p=warpToMouse(p); float n=max(6.0,uSym);
  vec2 q=fold(p,n); float r=length(p)+0.08; float a=atan(q.y,q.x);
  float depth=1.0/r + t*0.6; int oc=octaves();
  vec2 tp=vec2(a*3.0, depth);
  float pat=fbm(tp*2.0,oc)+0.5*fbm(tp*5.0 - t,oc)+0.5*sin(depth*6.0+a*n);
  vec3 col=PAL(fract(pat*0.6+depth*0.08-t*0.1), uPalette);
  col*=0.4+1.2*smoothstep(0.0,0.5,r);
  return col+PAL(fract(t*0.2),uPalette)*exp(-r*5.0)*1.2;
}
vec3 hyperspace3D(vec2 p, float t){
  p=warpToMouse(p);
  vec3 rd=normalize(vec3(p*1.1, 1.5));
  rd.xy = rot(t*0.12)*rd.xy;
  float N=max(5.0,uSym);
  vec3 acc=vec3(0.0); float z=0.4;
  for(int i=0;i<40;i++){
    vec3 q=vec3(rd.xy*z, z + t*2.2);          // scroll the lattice toward the viewer
    float a=atan(q.y,q.x), r=length(q.xy)+1e-3;
    a=mod(a,TAU/N); a=abs(a-PI/N);            // kaleidoscopic cross-section
    float cell = sin(a*N*1.5)*0.5 + sin(r*5.0 - q.z*1.8) + sin(q.z*2.0);
    float dens = smoothstep(0.3,1.0, 0.5+0.5*cell) * exp(-r*0.7);
    acc += PAL(fract(q.z*0.04 + 0.15*cell + t*0.05), uPalette) * dens * 0.06;
    z += 0.14;
  }
  return acc * (1.0/(1.0+dot(p,p)*0.25));
}
vec3 hyperbolic(vec2 p, float t){
  p=warpToMouse(p);
  vec2 z=p*0.5; float R=length(z); if(R>0.98) z*=0.98/R;
  float a=0.34*sin(t*0.13);                                   
  z=cdiv(vec2(z.x-a, z.y), vec2(1.0-a*z.x, -a*z.y));
  z=rot(t*0.05)*z;
  float P=max(3.0,floor(uSym));
  vec2 cc=vec2(1.15,0.0); float rho2=dot(cc,cc)-1.0;          
  float cell=0.0;
  for(int i=0;i<10;i++){
    float an=atan(z.y,z.x), r2=length(z); an=mod(an,TAU/P); an=abs(an-PI/P); z=r2*vec2(cos(an),sin(an));
    vec2 zz=z-cc; float dd=dot(zz,zz);
    if(dd<rho2){ z=cc+zz*(rho2/dd); cell+=1.0; } else break;
  }
  float v=cell*0.18 + 0.35*length(z) + 0.08*t;
  vec3 col=PAL(fract(v),uPalette)*(0.45+0.55*sin(cell*1.7+length(z)*4.0));
  return max(col,0.0);
}
vec3 entoptic(vec2 p, float t){
  vec3 col=vec3(0.015,0.015,0.03);
  float sn=hash21(floor(gl_FragCoord.xy)+floor(t*28.0)); col+=vec3(sn*sn)*0.22;
  for(int i=0;i<6;i++){ float fi=float(i);
    vec2 c=0.45*vec2(sin(t*0.3+fi*1.3),cos(t*0.23+fi*1.9));
    col+=PAL(fract(0.12*fi+0.05*t),uPalette)*0.05/(length(p-c)*7.0+0.3); }
  for(int i=0;i<24;i++){ float fi=float(i);
    float an=fi*2.4 + t*(1.4+0.4*sin(fi)); float rad=0.08+0.34*fract(fi*0.137+0.1*sin(t*0.2+fi));
    vec2 c=rad*vec2(cos(an),sin(an*0.9));
    col+=vec3(0.55,0.78,1.0)*0.018/(length(p-c)*70.0+0.4); }
  return col;
}
vec3 quasicrystal(vec2 p, float t){
  p=warpToMouse(p)*mix(3.0,8.0,uCplx);
  float N=clamp(floor(uSym),3.0,12.0); float v=0.0, ph=t*0.5 + uAudio.y*4.0;
  for(int i=0;i<12;i++){ if(float(i)>=N) break; float a=PI*float(i)/N; v+=cos(p.x*cos(a)+p.y*sin(a)+ph); }
  v/=N; float band=0.5+0.5*cos(v*PI*4.0 + t);
  return PAL(fract(v*0.6 + 0.1*t + uCentroid*0.3), uPalette)*(0.35+0.95*band);
}
vec3 cymatics(vec2 p, float t){
  p=warpToMouse(p);
  float n=floor(mix(2.0,9.0,uCplx))+floor(uAudio.z*5.0), m=floor(mix(1.0,7.0,uCplx))+floor(uAudio.w*5.0);
  float f=cos(n*PI*p.x*0.5)*cos(m*PI*p.y*0.5) - cos(m*PI*p.x*0.5)*cos(n*PI*p.y*0.5);
  f+=0.3*sin(t*0.5)*cos((n+m)*PI*length(p)*0.4);
  float nodal=smoothstep(0.07,0.0,abs(f));
  return PAL(fract(0.5+0.25*f+0.08*t),uPalette)*(0.12+nodal*1.1) + vec3(0.9,0.85,0.7)*nodal*0.4;
}
vec3 imageMode(vec2 p, float t){
  p=warpToMouse(p);
  vec2 q=fold(rot(t*0.05)*p, max(2.0,uSym));
  q += uAudio.z*0.12*vec2(sin(q.y*5.0+t), cos(q.x*5.0+t));
  vec2 uv=fract(q*mix(0.5,1.2,uCplx)+0.5);
  vec3 img=texture(uImage, uv).rgb; float lum=dot(img,vec3(0.299,0.587,0.114));
  img=mix(img, PAL(fract(lum+0.1*t), uPalette), 0.15+0.4*uAsc); 
  return img*(0.55+0.7*lum);
}

vec2 hexLocal(vec2 p){ const vec2 r=vec2(1.0,1.7320508), h=r*0.5;
  vec2 a=mod(p,r)-h, b=mod(p-h,r)-h; return dot(a,a)<dot(b,b)?a:b; }

vec3 voronoiMode(vec2 p, float t){
  p=warpToMouse(p);
  float scale=mix(2.5,7.5,uCplx) + uAudio.y*2.0;       
  int metric=int(mod(floor(uSym),3.0));                
  vec2 g=p*scale, ic=floor(g), f=fract(g);
  float F1=9.0,F2=9.0; vec2 id1=vec2(0.0);
  for(int j=-1;j<=1;j++) for(int i=-1;i<=1;i++){
    vec2 o=vec2(float(i),float(j)), cc=ic+o;
    vec2 jt=0.5+0.40*vec2(sin(t*0.7+TAU*hash21(cc)), cos(t*0.6+TAU*hash21(cc+7.1))); 
    vec2 r=o+jt-f;
    float d = metric==0 ? length(r) : metric==1 ? abs(r.x)+abs(r.y) : max(abs(r.x),abs(r.y));
    if(d<F1){ F2=F1; F1=d; id1=cc; } else if(d<F2){ F2=d; }
  }
  float wall=smoothstep(0.0,0.07,F2-F1);                 
  float dome=1.0-F1;                                      
  float ch=hash21(id1);
  vec3 base=PAL(fract(ch*0.7 + 0.10*t + uCentroid*0.3), uPalette);
  vec3 col=base*(0.28+0.95*dome*dome);                   
  col*=0.30+0.70*wall;                                    
  col+=PAL(fract(ch+0.5),uPalette)*(1.0-wall)*(0.45+0.4*uFlux); 
  return col*smoothstep(0.0,0.05,length(p));
}
vec3 phasorMode(vec2 p, float t){
  p=warpToMouse(p);
  float F=mix(2.0,6.5,uCplx) + uAudio.z*3.0;            
  float ang=fbm(p*0.55+vec2(0.0,t*0.05), octaves())*TAU + t*0.08 + uBands0.w*1.5; 
  float ks=1.3; vec2 g=p*ks, ic=floor(g);
  vec2 acc=vec2(0.0);
  for(int j=-1;j<=1;j++) for(int i=-1;i<=1;i++){
    vec2 o=vec2(float(i),float(j)), cc=ic+o; vec2 kp=(cc+0.5)/ks;
    float a2=ang+(hash21(cc)-0.5)*0.7; vec2 dir=vec2(cos(a2),sin(a2));
    float ph=TAU*F*dot(p-kp,dir)+TAU*hash21(cc+3.3);
    float d=length(p-kp)*ks; float w=exp(-d*d*1.3);
    acc+=w*vec2(cos(ph),sin(ph));                        
  }
  float phase=atan(acc.y,acc.x);                           
  float stripe=0.5+0.5*sin(phase);
  float vine=pow(stripe,3.0);                             
  vec3 col=PAL(fract(phase/TAU+0.08*t+uCentroid*0.3),uPalette)*(0.22+1.05*vine);
  col+=PAL(fract(0.5+phase/TAU),uPalette)*smoothstep(0.85,1.0,stripe)*(0.45+0.4*uFlux); 
  return col;
}
vec3 lizardMode(vec2 p, float t){
  vec2 uv=gl_FragCoord.xy/uRes; float asp=uRes.x/uRes.y;
  vec2 c=uv-0.5; if(asp>1.0) c.x*=asp; else c.y/=asp;     
  c=rot(t*0.02)*c;
  float scale=mix(11.0,34.0,uCplx);                       
  vec2 hp=c*scale; vec2 local=hexLocal(hp); vec2 center=hp-local;
  vec2 cellUv=clamp(center/scale+0.5,0.001,0.999);
  float s=texture(uField,cellUv).x;                       
  vec3 m0=PAL(0.03,uPalette), m1=PAL(0.40,uPalette);      
  vec3 col=mix(m0,m1, smoothstep(0.42,0.58,s));
  float dome=max(0.0,1.0-length(local)*1.05);             
  float gap=smoothstep(0.52,0.40,length(local));          
  col*=(0.40+0.62*dome)*gap;
  col+=PAL(fract(s*0.5+0.1*t),uPalette)*pow(dome,6.0)*0.35; 
  return col;
}
vec3 scopeMode(vec2 ignored, float t){
  vec2 q=(gl_FragCoord.xy-0.5*uRes)/uRes.y*2.4;            
  q=rot(t*0.03)*q;
  float r=length(q), a=atan(q.y,q.x);
  float s=abs(fract(a/TAU)*2.0-1.0);                        
  vec2 wv=texture(uWaveform, vec2(s,0.5)).rg*2.0-1.0;       
  float w=0.011 + 0.6/uRes.y;                              
  float dL=abs(r-(0.98+wv.x*0.52));                         
  float dR=abs(r-(0.60+wv.y*0.30));                         
  vec3 cL=PAL(fract(s*0.6 + 0.10*t + uCentroid*0.3), uPalette);
  vec3 cR=PAL(fract(s*0.6 + 0.5 + 0.10*t), uPalette);
  vec3 col = cL*smoothstep(w,0.0,dL)*1.1 + cL*smoothstep(0.05,0.0,dL)*0.10; 
  col += cR*smoothstep(w,0.0,dR)*0.9 + cR*smoothstep(0.05,0.0,dR)*0.08;     
  col += vec3(0.85,0.9,1.05)*smoothstep(0.03,0.0,r)*(0.35+uAudio.x*0.9);    
  return col*(0.9+uAudio.x*0.4);
}
float deMandelbox(vec3 p, float scale){
  vec3 z=p; float dr=1.0;
  for(int i=0;i<9;i++){
    z=clamp(z,-1.0,1.0)*2.0-z;                                   // box fold
    float r2=dot(z,z);
    if(r2<0.25){ z*=4.0; dr*=4.0; } else if(r2<1.0){ z/=r2; dr/=r2; }   // sphere fold
    z=z*scale+p; dr=dr*abs(scale)+1.0;
  }
  return length(z)/abs(dr);
}
vec3 fractalMode(vec2 q, float t){
  q=warpToMouse(q);
  vec3 ro=vec3(0.0,0.0,-3.4 + t*0.5);                            // fly forward through the structure
  vec3 rd=normalize(vec3(q*0.9, 1.4)); rd.xy=rot(t*0.05)*rd.xy; rd.yz=rot(t*0.03)*rd.yz;
  float scale=2.6 + 0.4*sin(t*0.1) + uAudio.y*0.4;              // bass morphs the fractal
  float tt=0.05, glow=0.0; bool hit=false; vec3 pos=ro;
  for(int i=0;i<90;i++){ pos=ro+rd*tt; float d=deMandelbox(pos, scale);
    glow += 0.008/(1.0+d*d*120.0);                               // tight inner halo only
    if(d<0.0012){ hit=true; break; } if(tt>18.0) break; tt += d*0.75; }
  vec3 col=vec3(0.0);
  if(hit){                                                       // real 3D surface shading → architecture, not soup
    vec2 e=vec2(0.0009,0.0);
    vec3 n=normalize(vec3(deMandelbox(pos+e.xyy,scale)-deMandelbox(pos-e.xyy,scale),
                          deMandelbox(pos+e.yxy,scale)-deMandelbox(pos-e.yxy,scale),
                          deMandelbox(pos+e.yyx,scale)-deMandelbox(pos-e.yyx,scale)));
    vec3 lig=normalize(vec3(0.6,0.7,-0.4));
    float dif=clamp(dot(n,lig),0.0,1.0), amb=0.22+0.28*n.y, ao=1.0/(1.0+tt*0.18), fog=exp(-tt*0.14);
    col = PAL(fract(0.14*tt + 0.3*dif + uCentroid*0.3 - t*0.04), uPalette)*(amb+dif)*ao*fog;
    col += PAL(fract(0.5+0.1*tt), uPalette)*pow(dif,10.0)*0.5*fog;   // specular glints
  }
  col += PAL(fract(0.6+glow*0.8), uPalette)*min(glow,1.2)*0.45;   // volumetric halo over everything
  return col*(0.9+uAudio.x*0.4);
}
vec3 particlesBg(vec2 p, float t){ float r=length(p);
  return PAL(fract(0.62 - r*0.07 + t*0.02), uPalette)*0.05*(1.0-r*0.22) + vec3(0.01,0.012,0.02); }

vec2 cmulC(vec2 a, vec2 b){ return vec2(a.x*b.x - a.y*b.y, a.x*b.y + a.y*b.x); } 
vec3 weierMode(vec2 p, float t){
  p = warpToMouse(p);
  vec2 z = p * mix(1.4, 3.2, uCplx);                       
  vec2  w1 = vec2(1.0, 0.0);
  float inhale = 1.0 - (uBands0.y*0.18 + uBeatPulse*0.10); 
  float reT = 0.5 + 0.4*sin(t*0.07 + uBands0.z*2.0);       
  float imT = mix(0.62, 0.95, uCplx) * inhale;             
  imT = max(imT, 0.30);                                    
  vec2  w2 = vec2(reT, imT);

  mat2 B  = mat2(w1, w2);                                  
  vec2 mn = inverse(B) * z;                                
  mn = floor(mn + 0.5);                                    
  z -= B * mn;                                             

  vec2 I  = vec2(1.0, 0.0);
  vec2 wp = cdiv(I, cmulC(z, z));                          
  for(int j=-3;j<=3;j++) for(int i=-3;i<=3;i++){
    if(i==0 && j==0) continue;
    vec2 om = w1*float(i) + w2*float(j);                   
    vec2 d  = z - om;
    wp += cdiv(I, cmulC(d, d)) - cdiv(I, cmulC(om, om));   
  }

  float ang  = atan(wp.y, wp.x);                           
  float mag  = length(wp) + 1e-6;                          
  float lm   = fract(log(mag)*0.5 - t*0.15);              
  float terr = smoothstep(0.0,0.08,lm) * smoothstep(1.0,0.92,lm);
  float ridgeN = max(2.0, floor(uSym));                    
  float phase  = abs(fract(ang/TAU*ridgeN) - 0.5) * 2.0;   

  vec3 col = PAL(fract(ang/TAU + 0.10*t + uCentroid*0.3), uPalette); 
  col *= (0.35 + 0.65*terr);                               
  col  = mix(col, col*0.22, smoothstep(0.25, 0.0, phase)); 
  float bloom = smoothstep(8.0, 42.0, mag) * (0.6 + 0.9*uBeat + 0.7*uBeatPulse); 
  col += PAL(fract(0.6), uPalette)*bloom*0.6 + vec3(bloom*bloom*0.5);            
  return max(col, 0.0);
}

vec2 cmul(vec2 a, vec2 b){ return vec2(a.x*b.x - a.y*b.y, a.x*b.y + a.y*b.x); }
vec2 cmulConj(vec2 a, vec2 b){ return vec2(a.x*b.x + a.y*b.y, a.x*b.y - a.y*b.x); }
vec3 blaschkeMode(vec2 p, float t){
  p = warpToMouse(p);
  vec2 z = p*0.62;
  float R = length(z);
  if(R > 0.985) z *= 0.985/R;
  R = length(z);

  int   n  = int(clamp(uSym, 2.0, 9.0));        
  float fn = float(n);

  float drift = 0.30 + 0.34*clamp(uBands0.y + 0.5*uAudio.y, 0.0, 1.0);
  drift = clamp(drift, 0.0, 0.86);
  float dph = t*0.10 + uBands0.w*1.6;
  vec2  c = drift*vec2(cos(dph)*sin(t*0.11), cos(t*0.09));   
  float cl = length(c); if(cl > 0.92) c *= 0.92/cl;          
  vec2  cc = vec2(c.x, -c.y);                                 
  float r0   = mix(0.42, 0.62, uCplx);          
  float rot0 = t*0.07;                           

  vec2  B  = vec2(1.0, 0.0);                      
  vec2  LB = vec2(0.0, 0.0);                      
  float nearZero = 1e9;                           

  for(int k=0; k<9; k++){
    if(k>=n) break;
    float ph = TAU*float(k)/fn + rot0;
    vec2 a0 = r0*vec2(cos(ph), sin(ph));         
    vec2 ak = cdiv(a0 + c, vec2(1.0,0.0) + cmul(cc, a0));
    float ar = length(ak); if(ar > 0.985) ak *= 0.985/ar;     

    vec2 num = z - ak;                            
    vec2 den = vec2(1.0,0.0) - cmulConj(ak, z);  
    B  = cmul(B, cdiv(num, den));
    LB += cdiv(vec2(1.0,0.0), num) + cdiv(vec2(ak.x,-ak.y), den); 
    nearZero = min(nearZero, length(num));
  }
  float th = t*0.13 + uCentroid*PI;
  B = cmul(B, vec2(cos(th), sin(th)));

  float thB  = atan(B.y, B.x);                    
  float magB = length(B);                         
  vec2  dB   = cmul(B, LB);                        
  float thD  = atan(dB.y, dB.x);                  
  float magD = length(dB);                         

  vec3 col = PAL(fract(thD/TAU + 0.10*t + uCentroid*0.3), uPalette);

  float iso = abs(fract(thB/TAU*fn) - 0.5)*2.0;
  float isoSharp = mix(0.40, 0.20, uCplx);        
  col *= 0.40 + 0.60*smoothstep(0.15, isoSharp, iso);

  float fil = smoothstep(0.28, 0.0, abs(magB - 1.0));
  float airAmt = 0.5 + 1.1*(uBands1.y + uBands1.z);
  col += PAL(fract(0.5 + thB/TAU), uPalette) * fil * airAmt * 0.6;

  float zeroGlow = exp(-nearZero*nearZero*26.0);
  col += PAL(fract(0.5), uPalette) * zeroGlow * (1.0 + 1.6*uBeatPulse) * 1.2;

  float crit = smoothstep(0.05, 0.0, magD);
  col += PAL(fract(0.72), uPalette) * crit * (0.7 + 1.4*uBands1.y) * 0.9;
  col *= smoothstep(1.0, 0.90, R/0.985);
  return max(col, 0.0);
}

vec2 mobius(vec4 m1, vec4 m2, vec2 z){
  vec2 num = cmul(m1.xy, z) + m1.zw;
  vec2 den = cmul(m2.xy, z) + m2.zw;
  return cdiv(num, den);
}
float mobDet(vec4 m1, vec4 m2){
  vec2 ad = cmul(m1.xy, m2.zw);   
  vec2 bc = cmul(m1.zw, m2.xy);  
  return length(ad - bc);
}

vec3 indraMode(vec2 p, float t){
  p = warpToMouse(p);
  float symN = clamp(floor(uSym), 2.0, 12.0);
  if(uSym > 2.5) p = fold(rot(t*0.04)*p, symN);
  vec2 z = p * 1.18;                       

  const float R = 1.5;                     
  float th  =  0.55*sin(t*0.10) + uBands0.w*1.4;
  float th2 = -0.55*cos(t*0.13) - uBands0.x*1.4;
  float phi =  t*0.06 + 0.30*sin(t*0.07);  

  float r = clamp(0.62 + uAudio.y*0.12 + uBands0.x*0.06, 0.40, 0.80);
  float r2 = r*r;

  vec2 cen[4];
  for(int k=0;k<4;k++){
    float ang = phi + float(k)*1.5707963;  
    cen[k] = R*vec2(cos(ang), sin(ang));
  }
  vec2 eA = r2*vec2(cos(th),  sin(th));
  vec2 eB = r2*vec2(cos(th2), sin(th2));

  vec4 g1[4]; vec4 g2[4];   
  vec2 Aa = cen[2];
  vec2 Ba = eA - cmul(cen[0], cen[2]);
  vec2 Ca = vec2(1.0, 0.0);
  vec2 Da = -cen[0];
  g1[0] = vec4(Aa, Ba);  g2[0] = vec4(Ca, Da);
  g1[1] = vec4(Da, -Ba); g2[1] = vec4(-Ca, Aa);
  vec2 Ab = cen[3];
  vec2 Bb = eB - cmul(cen[1], cen[3]);
  vec2 Cb = vec2(1.0, 0.0);
  vec2 Db = -cen[1];
  g1[2] = vec4(Ab, Bb);  g2[2] = vec4(Cb, Db);
  g1[3] = vec4(Db, -Bb); g2[3] = vec4(-Cb, Ab);

  vec2 ic[4];
  ic[0]=cen[0]; ic[1]=cen[2]; ic[2]=cen[1]; ic[3]=cen[3];

  int STEPS = int(mix(20.0, 40.0, clamp(uCplx + uAudio.z*0.4, 0.0, 1.0))); 
  float deriv = 1.0;          
  float trap  = 0.0;          
  float wordH = 0.0;          
  float mind  = 1e9;          
  bool escaped = false;

  for(int s=0;s<40;s++){
    if(s>=STEPS) break;
    int hit = -1;
    for(int k=0;k<4;k++){
      if(length(z - ic[k]) < r) hit = k;     
    }
    if(hit < 0){ escaped = true;
      for(int k=0;k<4;k++) mind = min(mind, abs(length(z - ic[k]) - r));
      break;
    }
    vec2 den = cmul(g2[hit].xy, z) + g2[hit].zw;
    float dd = mobDet(g1[hit], g2[hit]) / (dot(den,den) + 1e-6);
    deriv *= dd;
    z = mobius(g1[hit], g2[hit], z);
    wordH += float(hit) + 1.0 + 0.25*float(hit*hit);  
    trap  += 1.0;
  }
  float distLambda = escaped ? (mind / max(deriv, 1e-6)) : 0.0;

  float ang = atan(z.y, z.x);
  float hueK = fract(trap*0.055 + wordH*0.013 + uCentroid*0.30
                     + ang*0.04 + 0.04*t);
  vec3 col = PAL(hueK, uPalette);
  col *= 0.22 + 0.55*smoothstep(0.0, 0.06, distLambda);
  float flare = 1.0 + uFlux*1.4 + uBeat*1.1;
  col += PAL(fract(0.55 + uCentroid*0.3), uPalette)
         * exp(-distLambda*55.0) * 1.25 * flare;
  col *= 0.45 + 1.10*clamp(deriv*0.06, 0.0, 1.0);
  if(!escaped) col += PAL(fract(0.6 + uCentroid*0.3), uPalette) * (0.9 + 0.6*uBeat);
  col *= 1.0 / (1.0 + dot(p,p)*0.18);
  return max(col, 0.0);
}

float arnoldWind(float Omega, float K, float seed, int N){
  float th = seed;
  for(int n=0;n<20;n++){ th += Omega - (K/TAU)*sin(TAU*th); }    
  float th0 = th, steps = 0.0;
  for(int n=0;n<48;n++){                                          
    if(n>=N) break;
    th += Omega - (K/TAU)*sin(TAU*th);
    steps += 1.0;
  }
  return (th - th0)/max(steps, 1.0);                             
}

vec3 arnoldTongues(vec2 p, float t){
  p = warpToMouse(p);
  float click  = floor(uBeat*6.0)/6.0;
  float Omega0 = 0.50 + (uCentroid-0.5)*0.45 + 0.025*sin(t*0.05) + click*0.03;
  float Omega  = Omega0 + p.x*0.30;                              
  float energy = uBands1.y*0.55 + uAudio.x*0.35;
  float yy     = (p.y + 2.4)/4.8;                                
  float K      = (0.02 + yy*yy*(1.35 + energy*0.9));             
  K = clamp(K, 0.0, 1.7);
  int   N      = int(mix(30.0, 48.0, clamp(uCplx,0.0,1.0)));     
  float seed   = 0.137 + 0.05*sin(t*0.07);

  float dO  = 0.006;
  float W   = arnoldWind(Omega,      K, seed, N);
  float W2  = arnoldWind(Omega + dO, K, seed, N);
  float slope = abs(W2 - W)/dO;                                 
  float lock = 1.0 - smoothstep(0.12, 0.85, slope);            
  float edge = smoothstep(0.20, 0.55, slope) * (1.0 - smoothstep(1.6, 3.2, slope));
  edge *= lock + 0.15;                                          

  float q   = clamp(uSym, 2.0, 18.0);
  float Wq  = floor(W*q + 0.5)/q;                               
  float Wc  = mix(W, Wq, 0.55);
  vec3  col = PAL(fract(Wc + 0.07*t), uPalette);

  col *= 0.16 + 1.55*lock;                                      
  col += PAL(fract(0.30 + W*3.0 + t*0.10), uPalette) * edge * (0.40 + 0.7*uFlux);
  col += PAL(fract(W*5.0 - t*0.06), uPalette)
         * smoothstep(1.0, 1.45, K) * (0.08 + 0.22*energy)
         * (0.5 + 0.5*sin(W*38.0 + t));
  col *= 0.85 + 0.15*smoothstep(0.0, 0.6, length(p));
  return max(col, 0.0);
}

bool isPrimeI(int n){ if(n<2) return false; if(n==2) return true; if((n%2)==0) return false;
  for(int d=3; d<256; d+=2){ if(d*d>n) break; if((n%d)==0) return false; } return true; }
bool gaussPrime(int a, int b){ int A=a<0?-a:a, B=b<0?-b:b; int N=A*A+B*B;
  if(A==0 || B==0){ int m=A+B; return isPrimeI(m) && ((m%4)==3); }   
  return isPrimeI(N); }                                              
vec3 gaussMode(vec2 p, float t){
  p=warpToMouse(p); p=rot(t*0.02)*p;
  float scale=mix(3.5,9.0,uCplx);
  vec2 g=p*scale, ic=floor(g+0.5);
  vec3 col=vec3(0.0);
  for(int j=-2;j<=2;j++) for(int i=-2;i<=2;i++){
    vec2 lp=ic+vec2(float(i),float(j));
    int a=int(lp.x), b=int(lp.y);
    if(!gaussPrime(a,b)) continue;
    int N=a*a+b*b;
    float d=length(g-lp);
    float ring=0.5+0.5*cos(float(N)*0.20 - t*2.0 + d*5.0);          
    col += PAL(fract(float(N)*0.013 + uCentroid*0.3 + 0.05*t), uPalette)
           * exp(-d*d*2.2) * (0.5+1.2*ring) * (1.0+1.3*uBeatPulse); 
  }
  float halo=0.5+0.5*sin(length(g)*mix(2.0,6.0,uCplx) - t*1.5);     
  col += PAL(fract(0.55-0.02*t), uPalette) * halo*halo * 0.06;
  return max(col,0.0);
}

vec3 defectMode(vec2 p, float t){
  p=warpToMouse(p);
  int N=int(mix(3.0,8.0, clamp(uCplx+uAudio.z*0.3,0.0,1.0)));
  float phase=0.0, prox=0.0, nearest=1e9;
  for(int k=0;k<8;k++){ if(k>=N) break; float fk=float(k);
    vec2 c=1.7*vec2(sin(t*0.23+fk*2.39 + uBands0.w*1.5), cos(t*0.19+fk*1.71 + uBands0.x*1.2)); 
    float q = (k-(k/2)*2)==0 ? 1.0 : -1.0;                         
    vec2 d=p-c; phase += q*atan(d.y,d.x);                          
    float dl=length(d); prox += 0.6/(dl+0.25); nearest=min(nearest,dl);
  }
  float wave = cos(phase + prox*mix(1.0,3.0,uCplx) - t*3.0);        
  float front = smoothstep(-0.1,0.5, wave);                        
  vec3 col = PAL(fract(0.5+0.4*wave + uCentroid*0.3 - 0.03*t), uPalette) * (0.25+0.8*front);
  col += PAL(fract(0.6+0.1*t), uPalette) * pow(max(0.0,wave),10.0) * (0.4+0.6*uFlux); 
  col += vec3(1.0,0.95,0.9) * exp(-nearest*nearest*8.0) * (0.3+0.7*uBeat);            
  return max(col,0.0);
}

vec3 pentaMode(vec2 p, float t){
  p=warpToMouse(p); p=rot(t*0.015)*p; p*=mix(1.8,4.5,uCplx);
  float edge=1e9, lineGlow=0.0; int idxSum=0;
  for(int j=0;j<5;j++){ float a=TAU*float(j)/5.0 + 0.2;
    vec2 e=vec2(cos(a),sin(a));
    float proj=dot(p,e) + 0.2*float(j) + 0.15*sin(t*0.1+float(j));  
    float f=fract(proj), dl=min(f,1.0-f);
    edge=min(edge, dl); idxSum += int(floor(proj)); lineGlow += smoothstep(0.04,0.0,dl);
  }
  int cls = idxSum - (idxSum/5)*5; if(cls<0) cls+=5;                
  float hue = float(cls)/5.0;
  vec3 col = PAL(fract(hue + 0.1*t + uCentroid*0.3), uPalette) * (0.42+0.58*smoothstep(0.0,0.12,edge));
  col += PAL(fract(hue+0.5), uPalette) * lineGlow * (0.3+0.6*uBands1.y) * 0.4;   
  return max(col,0.0);
}

float lague(int k, float a, float x){
  if(k<=0) return 1.0;                                   
  if(k==1) return (1.0 + a) - x;                         
  return 0.5*x*x - (a+2.0)*x + 0.5*(a+1.0)*(a+2.0);
}

float legP0(int l, float c){
  if(l<=0) return 1.0;                                   
  if(l==1) return c;                                     
  if(l==2) return 0.5*(3.0*c*c - 1.0);                  
  return 0.5*c*(5.0*c*c - 3.0);                          
}

float radialNL(int n, int l, float r){
  float rho = 2.0*r/float(n);
  float pw  = pow(rho, float(l));                        
  float lag = lague(n-l-1, float(2*l+1), rho);           
  return pw * lag * exp(-0.5*rho);
}

vec3 orbitalMode(vec2 p, float t){
  p = warpToMouse(p) * 1.15;
  float r = length(p) + 1e-4;
  float c = p.y / r;                                     
  float order = clamp((uSym - 2.0)/16.0, 0.0, 1.0);     
  int   nA = 2, lA = 1;                                  
  int   nB = (uCplx > 0.55) ? 4 : 3;                     
  int   lB = (order > 0.55) ? 3 : 2;                     
  nB = max(nB, lB + 1);                                  
  float angA = legP0(lA, c);
  float angB = legP0(lB, c);
  float psiA = radialNL(nA, lA, r) * angA;
  float psiB = radialNL(nB, lB, r) * angB * 0.55;          
  float w = clamp(0.30 + 0.55*uCentroid, 0.05, 0.95);
  float wa = sqrt(1.0 - w), wb = sqrt(w);
  float omega = 1.10 + uAudio.y*2.6 + uBands0.y*1.6;
  float beat  = t*omega + uBeat*PI;
  float re   = wa*psiA + wb*psiB*cos(beat);
  float im   =           wb*psiB*sin(beat);
  float dens = re*re + im*im;                            
  float phase = atan(im, re);
  float gain = mix(1.6, 4.6, uCplx);
  float lum  = pow(clamp(dens*gain, 0.0, 1.0), 0.62);
  vec3 col = PAL(fract(phase/TAU + 0.12*r - 0.05*t + 0.30*uCentroid), uPalette) * lum;
  col += PAL(fract(0.5 + phase/TAU), uPalette) * smoothstep(0.55,1.0,lum) * (0.35 + 0.5*uFlux);
  float node = smoothstep(0.0, 0.045, sqrt(dens));       
  col *= mix(1.0, node, 0.6 + 0.4*uFlux);
  col *= smoothstep(0.0, 0.06, r);
  return max(col, 0.0);
}

vec3 denomMode(vec2 p, float t){
  p=warpToMouse(p);
  vec2 z=vec2(p.x*mix(0.5,1.1,uCplx), (p.y+2.55)*0.46);     
  z.x += 0.12*sin(t*0.05); z.y=max(z.y,0.015);
  int depth=0; float wall=1e9;
  for(int k=0;k<48;k++){
    z.x -= floor(z.x+0.5);                                  
    wall=min(wall, 0.5-abs(z.x));                           
    float r2=dot(z,z);
    wall=min(wall, abs(sqrt(r2)-1.0));                      
    if(r2<1.0){ z=-z/r2; depth++; } else break;             
  }
  float hue=fract(float(depth)*0.105 + z.x*0.6 + uCentroid*0.3 + 0.04*t);
  vec3 col=PAL(hue,uPalette)*(0.28+0.72*smoothstep(0.0,0.6,z.y));   
  col*=0.40+0.60*smoothstep(0.0,0.05,wall);                          
  col+=PAL(fract(hue+0.5),uPalette)*exp(-wall*26.0)*(0.3+0.7*uFlux); 
  return max(col,0.0);
}

vec3 waveMode(vec2 p, float t){
  p=warpToMouse(p);
  float scale=mix(1.8,4.5,uCplx);
  vec2 q=p*scale; vec2 cell=floor(q+0.5), f=q-cell;
  float om=0.55+0.30*(0.5+0.5*sin(t*0.2+uBands0.y*3.0));    
  float k=sqrt(max(1e-3,1.0-om*om)); float ph=om*(t*1.6);
  float ux=4.0*atan( (k/om)*sin(ph + cell.x*1.7) / cosh(k*f.x*3.2) );
  float uy=4.0*atan( (k/om)*cos(ph + cell.y*1.3) / cosh(k*f.y*3.2) );
  float field=sin(0.5*(ux+uy)); float amp=abs(field);
  vec3 col=PAL(fract(0.5+0.4*field + dot(cell,vec2(0.06,0.04)) + uCentroid*0.3 - 0.03*t), uPalette)*(0.28+0.72*amp);
  col+=PAL(fract(0.62),uPalette)*pow(amp,6.0)*(0.3+0.7*uBeatPulse); 
  return max(col,0.0);
}

vec3 phaseportal(vec2 p, float t){
  vec2 z = p * 0.85;
  int N = int(mix(6.0, 16.0, clamp(uCplx, 0.0, 1.0)));
  float bSub  = uBands0.x, bBass = uBands0.y, bLow = uBands0.z, bMid = uBands0.w;
  float bHigh = uBands1.x, bTre  = uBands1.y, bAir = uBands1.z;
  vec2 h = vec2(0.0);
  bool seeded = false;
  for(int i=0;i<16;i++){
    int k = N - i;                 
    if(k < 1) break;               
    float fk = float(k);
    float hx = hash21(vec2(fk, 11.3));
    float hy = hash21(vec2(fk, 27.7));
    float band = (k <= 2) ? (0.6 + 1.4*bBass + 0.8*bSub)         
               : (k <= 5) ? (0.9 + 1.2*bLow  + 0.9*bMid)         
                          : (1.3 + 1.6*bTre  + 1.1*bHigh + bAir); 
    float w = (0.10 + 0.05*fk) * band;     
    float pull = uFlux * 0.35 * sin(t*1.7 + fk*2.3);
    vec2 d = (0.55 - pull) * vec2(sin(t*w + hx*TAU), cos(t*w*0.83 + hy*TAU));
    vec2 c = 0.7 + 0.6*vec2(hx - 0.5, hy - 0.5);          
    vec2 bk = vec2(z.x*c.x - z.y*c.y, z.x*c.y + z.y*c.x) + d; 
    vec2 ak = 0.30 * vec2(cos(fk*1.9), sin(fk*1.9));
    if(!seeded){ h = bk; seeded = true; }       
    else        h = bk + cdiv(ak, h);           
  }
  vec2 f = h;
  float modu = length(f);
  float ph   = atan(f.y, f.x);                  
  float lm   = log(modu + 1e-4);                
  float hue = fract(ph/TAU + 0.04*t + uCentroid*0.30);
  vec3 col  = PAL(hue, uPalette);
  float rings = fract(lm * mix(1.5, 4.0, clamp(uCplx,0.0,1.0)) - t*0.30);
  rings = pow(rings, 1.0 + 1.5*uAudio.x);       
  col *= 0.35 + 0.90*rings;
  float poleDark = modu / (1.0 + modu);         
  col *= 0.22 + 0.78*poleDark;                  
  float zeroGlow = 1.0 / (0.06 + modu*modu);    
  vec3  coreCol  = mix(PAL(hue, uPalette), vec3(1.0), 0.6); 
  col += coreCol * clamp(zeroGlow*0.05, 0.0, 6.0) * (0.7 + 0.9*uFlux);
  col = hueShift(col, uFlux * 0.6 * rings);
  return max(col, 0.0);
}

vec3 vortexMode(vec2 p, float t){
  p=warpToMouse(p); p=rot(t*0.03)*p;
  float scale=mix(2.0,5.0,uCplx) + uAudio.y*1.0;
  vec2 g=p*scale;
  const vec2 b1=vec2(1.0,0.0), b2=vec2(0.5,0.8660254);          
  const mat2 Binv=mat2(1.1547,0.0,-0.5774,1.1547);             
  vec2 base=floor(Binv*g);
  float phase=0.0, amp=1.0, nearest=1e9;
  for(int j=-1;j<=1;j++) for(int i=-1;i<=1;i++){
    vec2 cell=base+vec2(float(i),float(j));
    vec2 v=cell.x*b1 + cell.y*b2;                               
    v += 0.10*vec2(sin(t*0.5+cell.x*1.7+uBands0.w), cos(t*0.4+cell.y*2.1)); 
    vec2 d=g-v; float dl=length(d);
    phase += atan(d.y,d.x);                                     
    amp *= tanh(dl*1.7);                                        
    nearest=min(nearest,dl);
  }
  float fringe=0.5+0.5*cos(phase - t*2.0 + uCentroid*3.0);      
  vec3 col=PAL(fract(phase/TAU + uCentroid*0.3 + 0.04*t), uPalette)*(0.2+0.85*amp);   
  col += PAL(fract(0.5+0.1*t), uPalette)*pow(fringe,4.0)*amp*0.4;
  col += vec3(0.7,0.85,1.0)*exp(-nearest*nearest*6.0)*(0.3+0.7*uBeatPulse);           
  return max(col,0.0);
}

/* ═══════════════════════════════════════════════════════════════════
   NEUROSCIENCE-GROUNDED MODES (29–34)
   ═══════════════════════════════════════════════════════════════════ */

// ── SACCADIC SUPPRESSION ─────────────────────────────────────────
// Microsaccade jitter + transient visual blanking. Dose widens the
// suppression window — the "frozen snapshot / strobe" of high-dose trips.
vec3 saccadicMode(vec2 p, float t){
  p = warpToMouse(p);
  // Microsaccade position: Brownian-like jitter driven by hash noise
  float msX = vnoise(vec2(t*18.0, 0.3)) * 2.0 - 1.0;
  float msY = vnoise(vec2(0.7, t*18.0)) * 2.0 - 1.0;
  float msAmp = 0.04 + uDose * 0.12;                          // amplitude scales with dose
  vec2 msP = p + vec2(msX, msY) * msAmp;

  // Saccade timing: ~3–5 Hz rhythmic events + stochastic noise
  float saccadePhase = fract(t * 3.5 + 0.3*hash21(vec2(floor(t*3.5), 9.1)));
  float suppressWidth = 0.08 + uDose * 0.22;                   // dose widens the blank
  float inSaccade = smoothstep(0.0, suppressWidth, saccadePhase)
                  * smoothstep(suppressWidth + 0.04, suppressWidth, saccadePhase);
  float suppress = inSaccade;

  // Frozen snapshot: snap position to discrete time steps
  float snapRate = 2.5 + uDose * 6.0;                         // more fragmentation at high dose
  float snapT = floor(t * snapRate) / snapRate;
  vec2 snapP = rot(snapT * 0.12) * msP;
  float r = length(snapP);
  float th = atan(snapP.y, snapP.x);
  float pat = sin(r * 12.0 - snapT * 2.0) * sin(th * 6.0);
  float act = smoothstep(-0.15, 0.35, pat);
  vec3 col = PAL(fract(pat * 0.35 + snapT * 0.04), uPalette) * (0.4 + 0.75*act);

  // Apply suppression — the screen blanks
  col *= 1.0 - suppress * (0.75 + uDose * 0.25);

  // Motion trail during saccade (persistence of vision)
  float trail = smoothstep(0.0, 0.12, suppress) * uDose;
  col += PAL(fract(t * 0.08 + 0.3), uPalette) * trail * 0.35;

  // Microsaccade spatial jitter overlay
  float jitter = abs(msX + msY) * uDose * 0.15;
  col += vec3(0.7, 0.8, 1.0) * jitter * 0.3;

  return col * smoothstep(0.0, 0.10, r);
}

// ── BINOCULAR RIVALRY ────────────────────────────────────────────
// Two competing perceptual interpretations alternate via adaptation.
// Dose slows alternation; extreme dose allows superposition.
vec3 rivalryMode(vec2 p, float t){
  p = warpToMouse(p);
  float r = length(p);
  float th = atan(p.y, p.x);

  // Two competing images (left eye: tunnel; right eye: spiral)
  float left  = sin(r * 14.0 - t * 1.2) * sin(th * 4.0 + t*0.15);
  float right = sin(r * 10.0 + th * 3.0 - t * 0.8) * cos(r * 5.0);

  // Adaptation dynamics — each image fatigues when dominant
  // We use a slow sine as adaptation proxy + noise for stochastic switches
  float adaptFreq = 0.7 * (1.0 - uDose * 0.6);                // dose slows switching
  float adaptL = 0.5 + 0.5 * sin(t * adaptFreq);
  float adaptR = 0.5 + 0.5 * sin(t * adaptFreq + PI);
  float noise = (vnoise(vec2(t * 1.5, 0.0)) - 0.5) * (0.3 + uDose * 0.3);

  float dominance = smoothstep(-0.15, 0.15, adaptL - adaptR + noise);

  // At high dose, both images can bleed through (perceptual blending)
  float blend = uDose * uDose * 0.5;

  vec3 colL = PAL(fract(left * 0.22 + 0.1), uPalette) * smoothstep(0.0, 0.18, abs(left));
  vec3 colR = PAL(fract(right * 0.22 + 0.55), uPalette) * smoothstep(0.0, 0.18, abs(right));

  vec3 col = mix(colL, colR, dominance);
  col = mix(col, colL + colR * 0.6, blend);                    // superposition

  // Transition flicker — during the switch, visual field flickers
  float switchSpeed = abs(dFdx(dominance) + dFdy(dominance)) * 10.0;
  float flicker = smoothstep(0.3, 1.0, switchSpeed) * 0.4;
  col += vec3(1.0) * flicker * uDose;

  return col * smoothstep(0.0, 0.10, r) * 0.9;
}

// ── CORTICAL TRAVELING WAVES ─────────────────────────────────────
// 2D wave equation with dispersion v(ω) = v₀√(1 + (ω/ωc)²).
// Multiple point sources on a cortical sheet, mapped through
// the log-polar retino-cortical transform.
vec3 travelingMode(vec2 p, float t){
  p = warpToMouse(p);
  float r = length(p) + 1e-3;
  float th = atan(p.y, p.x);

  // Dispersion relation
  float v0 = 0.3 + uDose * 0.2;
  float wc = 2.0 + uCplx * 3.0;
  float freq = mix(1.0, 5.0, uCplx) + uAudio.y * 2.0;
  float v = v0 * sqrt(1.0 + (freq/wc)*(freq/wc));

  // Multiple wave sources on the cortical sheet
  float wave = 0.0;
  for(int i = 0; i < 6; i++){
    float fi = float(i);
    vec2 src = 1.5 * vec2(cos(fi * 1.047), sin(fi * 1.047));
    // Wandering source positions
    src += 0.3 * vec2(sin(t * 0.2 + fi * 1.7), cos(t * 0.15 + fi * 2.3));
    float d = length(p - src);
    float phase = d * freq - t * v * freq + fi * 0.8;
    wave += sin(phase) * exp(-d * d * 0.4) * 0.18;
  }

  // Retino-cortical mapping: radial log compression
  float cx = log(1.0 + 0.586 * r);
  float corticalWave = sin(cx * 8.0 - t * 2.0) * cos(th * 3.0 + t * 0.3);

  float combined = wave + corticalWave * 0.4;
  float act = smoothstep(-0.25, 0.3, combined);

  vec3 col = PAL(fract(combined * 0.3 + cx * 0.12 - t * 0.03), uPalette) * (0.35 + 0.85*act);
  // Traveling wavefront highlight
  float front = smoothstep(0.0, 0.05, abs(combined));
  col += PAL(fract(combined * 0.5 + 0.5), uPalette) * (1.0 - front) * 0.4 * (0.5 + 0.5*uFlux);

  return col * smoothstep(0.0, 0.10, r);
}

// ── PHOTORECEPTOR BLEACHING ──────────────────────────────────────
// Simulates LMS cone bleaching with different recovery time constants.
// High dose accelerates cycles — "trailing" and "colour echo" effects.
vec3 bleachMode(vec2 p, float t){
  p = warpToMouse(p);
  float r = length(p);

  // Scene content
  float th = atan(p.y, p.x);
  float scene = sin(r * 8.0 - t * 1.5) * sin(th * 4.0 + t * 0.2);
  float sceneBright = 0.5 + 0.5 * scene;

  // LMS cone response (simplified: different spatial frequencies per cone)
  float L = 0.5 + 0.5 * sin(r * 6.0 + scene * 2.0 - t * 1.3);
  float M = 0.5 + 0.5 * sin(r * 6.0 + scene * 2.0 - t * 1.3 + 1.2);
  float S = 0.5 + 0.5 * sin(r * 6.0 + scene * 2.0 - t * 1.3 + 2.5);

  // Bleaching: each cone type has a different time constant
  float bleachRate = 0.5 + uDose * 3.0;                        // dose accelerates bleaching
  float tauL = 120.0, tauM = 90.0, tauS = 60.0;
  // Use cyclic time to simulate repeated bleaching
  float cycleT = mod(t * bleachRate, 60.0);
  float bleachL = 1.0 - exp(-cycleT / tauL * bleachRate);
  float bleachM = 1.0 - exp(-cycleT / tauM * bleachRate);
  float bleachS = 1.0 - exp(-cycleT / tauS * bleachRate);

  // Bleached signals (adapted state)
  float La = L * (1.0 - bleachL) + 0.5 * bleachL;
  float Ma = M * (1.0 - bleachM) + 0.5 * bleachM;
  float Sa = S * (1.0 - bleachS) + 0.5 * bleachS;

  // Afterimage = complement of adapted
  float afterL = 1.0 - La;
  float afterM = 1.0 - Ma;
  float afterS = 1.0 - Sa;

  // Convert simplified LMS → approximate RGB
  vec3 rgb = vec3(
    afterL * 0.95 + afterM * 0.15,
    afterL * 0.25 + afterM * 0.90 + afterS * 0.15,
    afterS * 0.90 + afterM * 0.20
  );

  // Periodic flash (re-stimulus)
  float flashCycle = fract(t * (0.5 + uDose * 1.5));
  float flash = smoothstep(0.0, 0.05, flashCycle) * smoothstep(0.15, 0.05, flashCycle);
  rgb = mix(rgb, vec3(1.0) - rgb, flash * uDose * 0.6);

  // Chromatic noise from bleaching
  rgb += (vec3(hash21(p * 50.0 + t), hash21(p * 70.0 + t + 3.0), hash21(p * 90.0 + t + 7.0)) - 0.5)
         * uDose * 0.08;

  return max(rgb, 0.0) * smoothstep(0.0, 0.10, r) * (0.5 + 0.7*sceneBright);
}

// ── PHOSPHENE FLICKER ────────────────────────────────────────────
// Temporal frequency tagging at 15–40 Hz elicits cortical resonance.
// Audio-reactive flicker frequency; specific bands produce specific patterns.
vec3 phospheneMode(vec2 p, float t){
  p = warpToMouse(p);
  float r = length(p);
  float th = atan(p.y, p.x);

  // Flicker frequency: base + audio + dose modulation
  float flickerFreq = 18.0 + uAudio.y * 14.0 + uDose * 8.0;
  float flicker = 0.5 + 0.5 * sin(t * flickerFreq * TAU);

  // Resonance enhancement at cortical preferred frequencies
  float fNorm = flickerFreq / 40.0;
  float resonance = 1.0;
  if(fNorm > 0.38 && fNorm < 0.58) resonance = 2.2;           // 15–23 Hz peak (alpha/beta)
  if(fNorm > 0.68 && fNorm < 0.88) resonance = 1.6;           // 27–35 Hz peak (gamma)

  // Geometric phosphenes: concentric rings + radial spokes modulated by flicker
  float ringPat = sin(r * 10.0 * flicker) * sin(th * floor(mix(3.0, 9.0, uCplx)));
  float spiralPat = sin(r * 8.0 + th * 3.0 - t * 1.5);
  float pat = mix(ringPat, spiralPat, 0.5 + 0.5*sin(t * 0.1));
  float act = smoothstep(-0.15, 0.35, pat * flicker * resonance);

  vec3 col = PAL(fract(pat * 0.3 + flickerFreq * 0.008 + t * 0.02), uPalette) * (0.3 + 0.85*act);
  col *= 0.5 + 0.5 * flicker;

  // Persistence / afterglow
  float persist = 0.1 + uDose * 0.3;
  float afterglow = smoothstep(0.0, persist, fract(t * flickerFreq * 0.08));
  col += PAL(fract(t * 0.04 + 0.5), uPalette) * afterglow * uDose * 0.18;

  // Flicker-induced gamma-band glow at centre
  col += vec3(0.6, 0.7, 1.0) * exp(-r * 4.0) * flicker * resonance * 0.15 * uDose;

  return col * smoothstep(0.0, 0.10, r);
}

// ── CHROMATIC ADAPTATION ─────────────────────────────────────────
// Von Kries adaptation with dose-dependent disruption.
// The eye's white balance drifts, creating impossible colours.
vec3 adaptationMode(vec2 p, float t){
  p = warpToMouse(p);
  float r = length(p);

  // Scene content
  float th = atan(p.y, p.x);
  float scene = sin(r * 6.0) * sin(th * 3.0 + t * 0.2);
  vec3 sceneCol = PAL(fract(scene * 0.22 + 0.1), uPalette);

  // Von Kries adaptation: divide each channel by adaptation state
  // Slow adaptation with time constant 2–5s
  float tau = 2.0 + uDose * 3.0;
  float adapt = 1.0 - exp(-mod(t, 30.0) / tau);

  // White point drift — dose perturbs the adapted white point
  vec3 whitePoint = vec3(
    0.5 + 0.5 * sin(t * 0.12 + uDose * 2.0),
    0.5 + 0.5 * sin(t * 0.12 + 1.1 + uDose * 1.5),
    0.5 + 0.5 * sin(t * 0.12 + 2.3 + uDose * 1.0)
  );

  // Adapt scene colour to white point (von Kries transform)
  vec3 adapted = sceneCol / (whitePoint + 0.15);
  adapted = mix(sceneCol, adapted, adapt);

  // Dose disruption: channel crosstalk, noise, temporal jitter
  float disruption = uDose * uDose * 0.6;
  adapted += (vec3(
    hash21(p * 40.0 + floor(t * 8.0)),
    hash21(p * 40.0 + floor(t * 8.0) + 5.0),
    hash21(p * 40.0 + floor(t * 8.0) + 11.0)
  ) - 0.5) * disruption;

  // Simultaneous contrast: colours shift based on surrounding context
  float surround = fbm(p * 2.0 + t * 0.05, 3);
  adapted = mix(adapted, adapted.grb, disruption * 0.25);

  // Chromatic aberration-like splitting at high dose
  float split = uDose * 0.04;
  adapted.r *= 1.0 + split * sin(r * 10.0 - t);
  adapted.b *= 1.0 - split * sin(r * 10.0 - t + 1.0);

  return max(adapted, 0.0) * smoothstep(0.0, 0.10, r) * (0.5 + 0.7*smoothstep(-0.3, 0.3, scene));
}

vec2 wallpaper(vec2 p){
  if(uWall<=0) return p;
  float s=max(0.4,uWallScale);                            
  vec2 q=p*s, f;
  if(uWall==1){                                            
    f=abs(fract(q*0.5)*2.0-1.0);
  } else if(uWall==2){                                     
    f=abs(fract(q*0.5)*2.0-1.0); if(f.x<f.y) f=f.yx;
  } else if(uWall==3){                                     
    const float SQ3=1.7320508;
    vec2 g=vec2(q.x - q.y/SQ3, 2.0*q.y/SQ3);             
    vec2 fr=fract(g); if(fr.x+fr.y>1.0) fr=1.0-fr.yx;    
    f=vec2(fr.x+0.5*fr.y, fr.y*SQ3*0.5);                  
  } else {                                                 
    return fold(hexLocal(q*1.1547), 6.0)*1.6;             
  }
  return (f-0.5)*2.6;                                      
}

void main() {
  vec2 p=(gl_FragCoord.xy-0.5*uRes)/uRes.y*2.4;
  float t=uTime*uSpeed;
  p *= 1.0 + uPulse*0.12*sin(uTime*uPulseRate);
  p *= 1.0 - (uBeat*0.018 + uBeatPulse*0.035);
  if(uAudio.z>0.001) p += uAudio.z*0.06*vec2(sin(p.y*4.0+uTime*1.3), cos(p.x*4.0+uTime*1.1));
  if(uChoreo>0.5){                                        
    p *= 1.0 - uBands0.x*0.07;                            
    p += uBands0.z*0.08*vec2(sin(p.y*6.0+uTime*2.2), cos(p.x*6.0+uTime*1.9)); 
    p = rot(uBands0.w*0.22*sin(uTime*0.8))*p;             
    float rr=length(p)/2.4;                               
    float wv=texture(uWaveform, vec2(clamp(rr,0.0,1.0),0.5)).r*2.0-1.0;
    p += normalize(p+1e-4) * wv * 0.16 * uAudioAmt;       
  }
  if(uShear>0.001){ p.x += uShear*0.55*sin(p.y*3.0 + t*1.7); p = rot(uShear*0.25*sin(t*0.6))*p; }
  if(uBuild>0.001){ p *= 1.0 - uBuild*0.16; p = rot(uBuild*uBuild*1.0*sin(uTime*2.5))*p; } 
  if(uDrop>0.001){ p *= 1.0 - uDrop*0.16; }                                                
  p = wallpaper(p);                                                                         
  
  vec3 col;
  if(uMode==0) col=formMode(p,t);
  else if(uMode==1) col=neuralMode(p,t);
  else if(uMode==2) col=turingMode();
  else if(uMode==3) col=sacred(p,t);
  else if(uMode==4) col=breathing(p,t);
  else if(uMode==5) col = uRay>0.5 ? hyperspace3D(p,t) : hyperspace(p,t);
  else if(uMode==6) col=hyperbolic(p,t);
  else if(uMode==7) col=entoptic(p,t);
  else if(uMode==8) col=quasicrystal(p,t);
  else if(uMode==9) col=cymatics(p,t);
  else if(uMode==10) col=imageMode(p,t);
  else if(uMode==11) col=voronoiMode(p,t);
  else if(uMode==12) col=phasorMode(p,t);
  else if(uMode==13) col=lizardMode(p,t);
  else if(uMode==14) col=scopeMode(p,t);
  else if(uMode==15) col=fractalMode(p,t);
  else if(uMode==16) col=particlesBg(p,t);
  else if(uMode==17) col=weierMode(p,t);
  else if(uMode==18) col=blaschkeMode(p,t);
  else if(uMode==19) col=indraMode(p,t);
  else if(uMode==20) col=arnoldTongues(p,t);
  else if(uMode==21) col=gaussMode(p,t);
  else if(uMode==22) col=defectMode(p,t);
  else if(uMode==23) col=pentaMode(p,t);
  else if(uMode==24) col=orbitalMode(p,t);
  else if(uMode==25) col=denomMode(p,t);
  else if(uMode==26) col=waveMode(p,t);
  else if(uMode==27) col=phaseportal(p,t);
  else if(uMode==28) col=vortexMode(p,t);
  else if(uMode==29) col=saccadicMode(p,t);
  else if(uMode==30) col=rivalryMode(p,t);
  else if(uMode==31) col=travelingMode(p,t);
  else if(uMode==32) col=bleachMode(p,t);
  else if(uMode==33) col=phospheneMode(p,t);
  else if(uMode==34) col=adaptationMode(p,t);
  else col=vortexMode(p,t);

  if(uAudioAmt>0.001 && uMode!=14){                       
    float rr=clamp(length(p)/2.4,0.0,1.0);
    float sp=texture(uSpectrum, vec2(rr,0.5)).r;
    col += PAL(fract(sp*0.6 + uCentroid*0.5 - uTime*0.04), uPalette) * sp*sp * uAudioAmt*0.5;
  }
  if(uChoreo>0.5 && uMode!=14){                           
    float rr=length(p)/2.4;
    float br=clamp(uBloomT,0.0,3.0);                      
    float ring=smoothstep(0.11,0.0, abs(rr - br*0.7)) * exp(-br*2.2) * (0.6+uFlux);
    col += PAL(fract(0.35 + uCentroid*0.4 + uTime*0.05), uPalette) * ring * 0.9; 
    col *= 1.0 + (p.x<0.0 ? uStereo.x : uStereo.y)*0.35;  
    col *= 1.0 + uBands0.x*0.25;                          
    float sp2=texture(uSpectrum, vec2(clamp(rr,0.,1.),0.5)).r;
    col = mix(col, PAL(fract(rr*1.2 - uTime*0.03), uPalette), sp2*0.22); 
    col += PAL(fract(rr*3.0 - uTime*0.1), uPalette) * (uBands1.x*0.30 + (uBands1.y+uBands1.z)*0.18) * (0.5+0.5*sin(rr*42.0)); 
  }
  col *= 1.0 + uAudio.x*0.16 + uAudio.y*0.18 + uBeat*0.14 + uBeatPulse*0.16; 
  if(uAsc>0.001){ col = mix(col, col + col.gbr*0.6, uAsc*0.22); col *= 1.0 + uAsc*0.15; } 
  if(uFeedback>0.001){
    vec2 c=vUv-0.5; c=rot(uFeedRot)*(c*uFeedZoom);
    col=max(col, texture(uScenePrev,c+0.5).rgb*uFeedback);
  }
  frag=vec4(col,1.0);
}
    `;

    const PRESENT = `#version 300 es
${COMMON}
uniform sampler2D uScene; uniform float uGrain, uChroma, uVignette, uSat;
uniform sampler2D uTransTex; uniform float uTrans; uniform int uTransMode;
uniform sampler2D uBloom; uniform float uBloomAmt;
in vec2 vUv; out vec4 frag;

vec3 sceneAt(vec2 uv){
  vec3 n=texture(uScene,uv).rgb;
  if(uTrans<=0.001) return n;
  float t=uTrans, p=1.0-t;
  if(uTransMode==0) return mix(n, texture(uTransTex,uv).rgb, t);
  return mix(n, texture(uTransTex,uv).rgb, t);
}

void main() {
  vec2 uv=vUv; vec3 col;
  float ca=uChroma*0.006; vec2 d=uv-0.5;
  col.r=sceneAt(uv+d*ca).r; col.g=sceneAt(uv).g; col.b=sceneAt(uv-d*ca).b;
  vec3 lab=lin2oklab(max(col,0.0)); lab.yz*=uSat*(1.0+uAudio.y*0.4+uAsc*0.5); col=oklab2lin(lab);
  col=hueShift(col, uHueShift + uCentroid*uAudioAmt*1.0);
  col*=1.0 + uPulse*0.20*sin(uTime*uPulseRate);

  // ── HDR Bloom (SOTA: mipmap-like soft wide glow) ──
  vec3 bloom = texture(uBloom, uv).rgb;
  float bloomStr = uBloomAmt * (1.0 + uBeat*0.8 + uAudioAmt*0.3);
  col += bloom * bloomStr;

  // ── Dissociative desaturation (K-hole coldness on dissonance) ──
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(col, vec3(lum) * vec3(0.55, 0.65, 1.0), uDissonance * 0.35);

  float gr=hash21(gl_FragCoord.xy+floor(uTime*40.0)); col+=(gr-0.5)*(uGrain + uAudio.w*0.05);
  float vig=smoothstep(1.35,0.25,length((uv-0.5)*vec2(uRes.x/uRes.y,1.0))); col*=mix(1.0,vig,uVignette);
  col=aces(max(col,0.0));
  col=mix(col, col*col*(3.0-2.0*col), uTone);
  col=pow(col,vec3(0.4545));
  frag=vec4(col,1.0);
}
    `;

    const BLOOM_BRIGHT = `#version 300 es
precision highp float;
uniform sampler2D uScene; uniform vec2 uRes;
in vec2 vUv; out vec4 frag;
void main(){
  vec3 c = texture(uScene, vUv).rgb;
  float b = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float contrib = smoothstep(0.25, 0.6, b);
  frag = vec4(c * contrib, 1.0);
}`;

    const BLOOM_BLUR = `#version 300 es
precision highp float;
uniform sampler2D uTex; uniform vec2 uDir;
in vec2 vUv; out vec4 frag;
void main(){
  vec3 c = texture(uTex, vUv).rgb * 0.227027;
  for(int i=1;i<5;i++){
    float fi=float(i);
    vec2 off = uDir * fi;
    c += texture(uTex, vUv + off).rgb * (0.1945946 / (1.0 + fi*0.3));
    c += texture(uTex, vUv - off).rgb * (0.1945946 / (1.0 + fi*0.3));
  }
  frag = vec4(c, 1.0);
}`;

    this.programs.field = this.program(VERT, FIELD);
    this.programs.compose = this.program(VERT, COMPOSE);
    this.programs.present = this.program(VERT, PRESENT);
    this.programs.bloomBright = this.program(VERT, BLOOM_BRIGHT);
    this.programs.bloomBlur = this.program(VERT, BLOOM_BLUR);
  }
};
